//===1.IMPORTS_AND_SETUP=Start==
import React, { useState, useCallback, useRef, useEffect } from "react";
import {
  Archive,
  File as FileIcon,
  Folder,
  Download,
  Upload,
  Shield,
  Eye,
  EyeOff,
  Trash2,
  Plus,
  FileArchive,
  Key,
  FilePlus,
  FolderPlus,
  Edit,
  RotateCcw, // Undo Icon
  RotateCw, // Redo Icon
  Share2, // Share Icon
  Clipboard, // Clipboard Icon
  ZoomIn, // Zoom In Icon
  ZoomOut, // Zoom Out Icon
  Expand, // Fit to Page Icon
  X, // Close Icon
  Loader2, // Loading Spinner Icon
  FileBox, // Box icon for split files
  Lock, // Lock Icon for Password Generation
  // ▼▼▼ DOWNLOAD-LIST-MODAL ▼▼▼
  ListChecks, // Icon for download list
  // ▲▲▲ DOWNLOAD-LIST-MODAL ▲▲▲
} from "lucide-react";
//===1.IMPORTS_AND_SETUP=End==

//===2_1.SUB_COMPONENTS_Progress_And_Confirm_Modals=Start==
const ProgressModal = ({ title, message, progress }) => {
  return (
    <div className="absolute inset-0 bg-slate-900 bg-opacity-60 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md">
        <h3 className="text-xl font-bold text-slate-800 mb-4">{title}</h3>
        <p className="text-slate-600 mb-5 text-sm truncate">{message}</p>
        <div className="w-full bg-slate-200 rounded-full h-2.5">
          <div
            className="bg-violet-500 h-2.5 rounded-full transition-all duration-150"
            style={{ width: `${progress}%` }}
          ></div>
        </div>
        <p className="text-right text-slate-500 font-mono text-sm mt-2">
          {progress}%
        </p>
      </div>
    </div>
  );
};

const ConfirmationModal = ({
  title,
  message,
  confirmText,
  onConfirm,
  onCancel,
  children,
}) => {
  return (
    <div className="absolute inset-0 bg-slate-900 bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm">
        <h3 className="text-lg font-bold text-slate-800 mb-4">{title}</h3>
        <div className="text-slate-600 mb-6 break-words">{message}</div>
        <div className="flex justify-end gap-4">
          {children || (
            <>
              <button
                onClick={onCancel}
                className="px-4 py-2 rounded-lg bg-slate-200 text-slate-700 hover:bg-slate-300 transition-colors"
              >
                キャンセル
              </button>
              <button
                onClick={onConfirm}
                className="px-4 py-2 rounded-lg bg-violet-500 text-white hover:bg-violet-600 transition-colors"
              >
                {confirmText}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ▼▼▼ DOWNLOAD-LIST-MODAL ▼▼▼
const DownloadListModal = ({ downloads, onDownload, onClose }) => {
  const [downloaded, setDownloaded] = useState(new Set());

  const handleDownload = (item, index) => {
    onDownload(item.blob, item.name);
    setDownloaded((prev) => new Set(prev).add(index));
  };

  const handleDownloadAll = async () => {
    for (let i = 0; i < downloads.length; i++) {
      if (!downloaded.has(i)) {
        handleDownload(downloads[i], i);
        // わずかな遅延を入れてブラウザのブロックを防ぐ試み
        await new Promise((resolve) => setTimeout(resolve, 300));
      }
    }
  };

  return (
    <div className="absolute inset-0 bg-slate-900 bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg">
        <div className="flex items-start gap-4 mb-4">
          <div className="bg-violet-100 text-violet-600 p-3 rounded-full">
            <ListChecks size={24} />
          </div>
          <div>
            <h3 className="text-lg font-bold text-slate-800">
              分割ファイルの準備ができました
            </h3>
            <p className="text-sm text-slate-600 mt-1">
              以下のファイルをすべてダウンロードしてください。
            </p>
          </div>
        </div>

        <div className="max-h-60 overflow-y-auto pr-2 border-t border-b py-3 my-4">
          <ul className="text-sm space-y-2">
            {downloads.map((item, index) => (
              <li
                key={item.name}
                className="flex items-center justify-between bg-slate-50 p-2 rounded-md"
              >
                <span
                  className={`truncate ${
                    downloaded.has(index)
                      ? "text-slate-400 line-through"
                      : "text-slate-700"
                  }`}
                >
                  {item.name}
                </span>
                <button
                  onClick={() => handleDownload(item, index)}
                  disabled={downloaded.has(index)}
                  className="px-3 py-1 text-xs font-semibold rounded-full bg-violet-500 text-white hover:bg-violet-600 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors"
                >
                  {downloaded.has(index) ? "済" : "保存"}
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="flex justify-between items-center">
          <button
            onClick={handleDownloadAll}
            className="px-4 py-2 text-sm rounded-lg bg-slate-200 text-slate-700 hover:bg-slate-300 transition-colors"
          >
            すべてダウンロード
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg bg-violet-500 text-white hover:bg-violet-600 transition-colors"
          >
            閉じる
          </button>
        </div>
        <p className="text-xs text-slate-400 mt-3 text-center">
          ※「すべてダウンロード」は、ブラウザの設定によりブロックされる場合があります。
        </p>
      </div>
    </div>
  );
};
// ▲▲▲ DOWNLOAD-LIST-MODAL ▲▲▲

// ▼▼▼ FEATURE-SPLIT-ZIP (zip.js version) ▼▼▼
const SplitRestoreModal = ({ initialFile, onUnzip, onCancel }) => {
  const [splitFiles, setSplitFiles] = useState([initialFile]);
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);

  const getFileBaseName = (filename) => {
    return filename.replace(/\.zip\.\d{3}$/i, "");
  };

  const getFileIndex = (filename) => {
    const match = filename.match(/\.(\d{3})$/i);
    return match ? parseInt(match[1], 10) : Infinity; // match[1]でキャプチャグループを取得
  };

  const baseName = getFileBaseName(initialFile.name);

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounter.current = 0;
    const newFiles = Array.from(e.dataTransfer.files).filter(
      (f) => getFileBaseName(f.name) === baseName
    );
    setSplitFiles((prev) =>
      [...prev, ...newFiles].reduce((acc, file) => {
        if (!acc.some((f) => f.name === file.name)) {
          acc.push(file);
        }
        return acc;
      }, [])
    );
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (dragCounter.current === 1) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleUnzipClick = () => {
    const sortedFiles = [...splitFiles].sort(
      (a, b) => getFileIndex(a.name) - getFileIndex(b.name)
    );
    onUnzip(sortedFiles);
  };

  return (
    <div className="absolute inset-0 bg-slate-900 bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg">
        <h3 className="text-lg font-bold text-slate-800 mb-2">
          分割ZIPファイルの結合
        </h3>
        <p className="text-sm text-slate-600 mb-4">
          分割された残りのZIPファイルをすべて追加してください。
        </p>

        <div
          onDrop={handleDrop}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
            isDragging ? "border-violet-500 bg-violet-50" : "border-slate-300"
          }`}
        >
          <div className="pointer-events-none">
            <FileBox size={48} className="mx-auto text-slate-400 mb-2" />
            <p className="text-slate-500">
              ここに残りのファイルをドラッグ＆ドロップ
            </p>
          </div>
        </div>

        <div className="mt-4 max-h-40 overflow-y-auto pr-2">
          <h4 className="text-sm font-semibold text-slate-700 mb-2">
            追加されたファイル:
          </h4>
          <ul className="text-xs space-y-1">
            {splitFiles
              .sort((a, b) => getFileIndex(a.name) - getFileIndex(b.name))
              .map((file) => (
                <li
                  key={file.name}
                  className="bg-slate-100 p-1.5 rounded-md truncate"
                >
                  {file.name}
                </li>
              ))}
          </ul>
        </div>

        <div className="flex justify-end gap-4 mt-6">
          <button
            onClick={onCancel}
            className="px-4 py-2 rounded-lg bg-slate-200 text-slate-700 hover:bg-slate-300 transition-colors"
          >
            キャンセル
          </button>
          <button
            onClick={handleUnzipClick}
            className="px-4 py-2 rounded-lg bg-violet-500 text-white hover:bg-violet-600 transition-colors"
          >
            結合して展開
          </button>
        </div>
      </div>
    </div>
  );
};
// ▲▲▲ FEATURE-SPLIT-ZIP (zip.js version) ▲▲▲
//===2_1.SUB_COMPONENTS_Progress_And_Confirm_Modals=End==

//===2_2.SUB_COMPONENTS_Preview_Modals=Start==
const PreviewToolbar = ({
  scale,
  setScale,
  onFit,
  pageInfo = null,
  onPageChange = null,
}) => {
  const zoomIn = () => setScale((s) => s + 0.25);
  const zoomOut = () => setScale((s) => Math.max(0.25, s - 0.25));

  return (
    <footer className="flex items-center justify-center p-2 border-t shrink-0 gap-2 sm:gap-4 bg-white">
      {pageInfo && (
        <>
          <button
            onClick={() => onPageChange(pageInfo.current - 1)}
            disabled={pageInfo.current <= 1}
            className="px-3 sm:px-4 py-2 text-sm rounded-lg bg-slate-200 text-slate-700 hover:bg-slate-300 transition-colors disabled:opacity-50"
          >
            前へ
          </button>
          <span className="text-sm font-medium text-slate-600">
            {pageInfo.current} / {pageInfo.total}
          </span>
          <button
            onClick={() => onPageChange(pageInfo.current + 1)}
            disabled={pageInfo.current >= pageInfo.total}
            className="px-3 sm:px-4 py-2 text-sm rounded-lg bg-slate-200 text-slate-700 hover:bg-slate-300 transition-colors disabled:opacity-50"
          >
            次へ
          </button>
          <div className="h-6 border-l border-slate-300 mx-2"></div>
        </>
      )}
      <button
        onClick={zoomOut}
        className="p-2 rounded-lg bg-slate-200 text-slate-700 hover:bg-slate-300 transition-colors"
        title="縮小"
      >
        <ZoomOut size={18} />
      </button>
      <button
        onClick={onFit}
        className="px-3 sm:px-4 py-2 text-sm font-mono rounded-lg bg-slate-200 text-slate-700 hover:bg-slate-300 transition-colors"
        title="全体表示"
      >
        {Math.round(scale * 100)}%
      </button>
      <button
        onClick={zoomIn}
        className="p-2 rounded-lg bg-slate-200 text-slate-700 hover:bg-slate-300 transition-colors"
        title="拡大"
      >
        <ZoomIn size={18} />
      </button>
    </footer>
  );
};

const PreviewModal = ({ file, content, onClose, onDownload }) => {
  const [scale, setScale] = useState(1.0);
  const [imgSize, setImgSize] = useState({ width: 0, height: 0 });
  const containerRef = useRef(null);

  const isImage = content.startsWith("data:image");

  const fitToScreen = useCallback(() => {
    if (!containerRef.current || !imgSize.width || !imgSize.height) return;
    const { clientWidth: cWidth, clientHeight: cHeight } = containerRef.current;
    const { width: iWidth, height: iHeight } = imgSize;

    const scaleX = cWidth / iWidth;
    const scaleY = cHeight / iHeight;
    setScale(Math.min(scaleX, scaleY, 2.0)); // 最大200%まで
  }, [imgSize]);

  useEffect(() => {
    if (imgSize.width > 0) {
      fitToScreen();
    }
  }, [imgSize, fitToScreen]);

  return (
    <div
      onClick={onClose}
      className="absolute inset-0 bg-slate-900 bg-opacity-75 flex items-center justify-center z-50 p-4 sm:p-8"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-lg shadow-xl w-full h-full max-w-6xl flex flex-col"
      >
        <header className="flex items-center justify-between p-3 border-b shrink-0">
          <span className="font-bold text-slate-700 truncate">{file.name}</span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onDownload(file)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-violet-500 text-white hover:bg-violet-600 transition-colors text-sm"
            >
              <Download size={16} />
              <span className="hidden sm:inline">ダウンロード</span>
            </button>
            <button
              onClick={onClose}
              className="p-2 sm:px-3 sm:py-1.5 rounded-lg bg-slate-200 text-slate-700 hover:bg-slate-300 transition-colors"
            >
              <X size={18} className="block sm:hidden" />
              <span className="hidden sm:inline">閉じる</span>
            </button>
          </div>
        </header>
        <div
          ref={containerRef}
          className="bg-slate-100 p-4 overflow-auto flex-1 flex items-center justify-center"
        >
          {isImage ? (
            <img
              src={content}
              alt={file.name}
              onLoad={(e) =>
                setImgSize({
                  width: e.target.naturalWidth,
                  height: e.target.naturalHeight,
                })
              }
              style={{
                transform: `scale(${scale})`,
                transformOrigin: "center center",
                transition: "transform 0.1s ease-in-out",
                maxWidth: "none",
                maxHeight: "none",
              }}
            />
          ) : (
            <pre className="text-sm whitespace-pre-wrap bg-white p-4 rounded">
              {content}
            </pre>
          )}
        </div>
        {isImage && (
          <PreviewToolbar
            scale={scale}
            setScale={setScale}
            onFit={fitToScreen}
          />
        )}
      </div>
    </div>
  );
};

const CsvPreviewModal = ({
  file,
  content,
  buffer,
  detectedEncoding,
  onClose,
  onDownload,
}) => {
  const [data, setData] = useState({ headers: [], rows: [] });
  const [selectedEncoding, setSelectedEncoding] = useState("auto");

  useEffect(() => {
    const parseCsv = (csvContent) => {
      if (!csvContent) {
        setData({ headers: [], rows: [] });
        return;
      }
      const lowerCaseName = file.name.toLowerCase();
      const delimiter = lowerCaseName.endsWith(".tsv") ? "\t" : ",";
      const lines = csvContent.trim().split("\n");

      // ▼▼▼【回避策適用】CSVの配列アクセス箇所 (空ファイルチェック)▼▼▼
      const firstLineIndex = 0;
      if (lines.length === 1 && lines[firstLineIndex].trim() === "") {
        setData({ headers: [], rows: [] });
        return;
      }

      // ▼▼▼【回避策適用】CSVの配列アクセス箇所 (ヘッダー解析)▼▼▼
      const headers = lines[firstLineIndex]
        .split(delimiter)
        .map((h) => h.trim());
      const rows = lines.slice(1).map((line) => {
        return line.split(delimiter).map((cell) => cell.trim());
      });
      setData({ headers, rows });
    };

    if (selectedEncoding === "auto") {
      parseCsv(content);
    } else {
      try {
        const decoder = new TextDecoder(selectedEncoding.toLowerCase());
        const decodedContent = decoder.decode(buffer);
        parseCsv(decodedContent);
      } catch (e) {
        setData({
          headers: [`${selectedEncoding}でのデコードに失敗しました。`],
          rows: [],
        });
      }
    }
  }, [selectedEncoding, content, buffer, file.name]);

  return (
    <div
      onClick={onClose}
      className="absolute inset-0 bg-slate-900 bg-opacity-75 flex items-center justify-center z-50 p-4 sm:p-8"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-lg shadow-xl w-full h-full max-w-6xl flex flex-col"
      >
        <header className="flex items-center justify-between p-3 border-b shrink-0">
          <span className="font-bold text-slate-700 truncate">{file.name}</span>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5">
              <label
                htmlFor="encoding-select"
                className="text-sm text-slate-600 hidden sm:inline"
              >
                文字コード:
              </label>
              <select
                id="encoding-select"
                value={selectedEncoding}
                onChange={(e) => setSelectedEncoding(e.target.value)}
                className="text-sm rounded border-slate-300 shadow-sm focus:border-violet-300 focus:ring focus:ring-violet-200 focus:ring-opacity-50 py-1"
              >
                <option value="auto">{`自動判別 (${
                  detectedEncoding || "N/A"
                })`}</option>
                <option value="UTF-8">UTF-8</option>
                <option value="Shift_JIS">Shift_JIS</option>
                <option value="EUC-JP">EUC-JP</option>
              </select>
            </div>
            <button
              onClick={() => onDownload(file)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-violet-500 text-white hover:bg-violet-600 transition-colors text-sm"
            >
              <Download size={16} />
              <span className="hidden sm:inline">ダウンロード</span>
            </button>
            <button
              onClick={onClose}
              className="p-2 sm:px-3 sm:py-1.5 rounded-lg bg-slate-200 text-slate-700 hover:bg-slate-300 transition-colors"
            >
              <X size={18} className="block sm:hidden" />
              <span className="hidden sm:inline">閉じる</span>
            </button>
          </div>
        </header>
        <div className="bg-slate-100 p-4 overflow-auto flex-1">
          <table className="w-full text-sm border-collapse bg-white shadow-md">
            <thead className="sticky top-0 bg-slate-200">
              <tr>
                {data.headers.map((header, index) => (
                  <th
                    key={index}
                    className="p-2 border border-slate-300 font-bold text-slate-700 text-left"
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.rows.map((row, rowIndex) => (
                <tr key={rowIndex} className="hover:bg-slate-50">
                  {row.map((cell, cellIndex) => (
                    <td
                      key={cellIndex}
                      className="p-2 border border-slate-300 text-slate-600"
                    >
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const PdfPreviewModal = ({ file, content, onClose, onDownload }) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [pageNum, setPageNum] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const [scale, setScale] = useState(1.0);
  const [isRendering, setIsRendering] = useState(true);

  useEffect(() => {
    const loadPdf = async () => {
      if (!content) return;
      const { pdfjsLib } = globalThis;
      if (!pdfjsLib) return;
      pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/5.4.54/pdf.worker.min.mjs`;

      try {
        const arrayBuffer = await content.arrayBuffer();
        const doc = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        setPdfDoc(doc);
        setNumPages(doc.numPages);
      } catch (error) {
        onClose();
      }
    };
    loadPdf();
  }, [content, onClose]);

  const fitToScreen = useCallback(async () => {
    if (!pdfDoc || !containerRef.current) return;
    const page = await pdfDoc.getPage(pageNum);
    const { clientWidth: cWidth, clientHeight: cHeight } = containerRef.current;
    const viewport = page.getViewport({ scale: 1.0 });
    const { width: pWidth, height: pHeight } = viewport;

    const scaleX = (cWidth - 20) / pWidth;
    const scaleY = (cHeight - 20) / pHeight;
    setScale(Math.min(scaleX, scaleY));
  }, [pdfDoc, pageNum]);

  useEffect(() => {
    if (pdfDoc) {
      fitToScreen();
    }
  }, [pdfDoc, fitToScreen]);

  useEffect(() => {
    let renderTask = null;
    const renderPage = async () => {
      if (!pdfDoc || !canvasRef.current) return;
      renderTask?.cancel();
      setIsRendering(true);

      const page = await pdfDoc.getPage(pageNum);
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      const viewport = page.getViewport({ scale });
      canvas.height = viewport.height;
      canvas.width = viewport.width;

      renderTask = page.render({ canvasContext: ctx, viewport });
      try {
        await renderTask.promise;
      } catch (error) {
        if (error.name !== "RenderingCancelledException") {
          console.error("PDF rendering error:", error);
        }
      } finally {
        setIsRendering(false);
      }
    };
    renderPage();

    return () => {
      renderTask?.cancel();
    };
  }, [pdfDoc, pageNum, scale]);

  return (
    <div
      onClick={onClose}
      className="absolute inset-0 bg-slate-900 bg-opacity-75 flex items-center justify-center z-50 p-4 sm:p-8"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-lg shadow-xl w-full h-full max-w-6xl flex flex-col"
      >
        <header className="flex items-center justify-between p-3 border-b shrink-0">
          <span className="font-bold text-slate-700 truncate">{file.name}</span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onDownload(file)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-violet-500 text-white hover:bg-violet-600 transition-colors text-sm"
            >
              <Download size={16} />
              <span className="hidden sm:inline">ダウンロード</span>
            </button>
            <button
              onClick={onClose}
              className="p-2 sm:px-3 sm:py-1.5 rounded-lg bg-slate-200 text-slate-700 hover:bg-slate-300 transition-colors"
            >
              <X size={18} className="block sm:hidden" />
              <span className="hidden sm:inline">閉じる</span>
            </button>
          </div>
        </header>
        <div
          ref={containerRef}
          className="relative bg-slate-100 p-4 overflow-auto flex-1 flex items-center justify-center"
        >
          {/* ▼▼▼ UI-FIX: Centered Loading Spinner ▼▼▼ */}
          {/* isRendering状態に応じて、ローディング表示とCanvas表示を切り替える */}
          {isRendering && (
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center justify-center text-slate-500">
              <Loader2 size={48} className="animate-spin mb-4" />
              <p>PDFを読み込んでいます...</p>
            </div>
          )}
          <canvas
            ref={canvasRef}
            className={`shadow-lg transition-opacity duration-300 ${
              isRendering ? "opacity-0" : "opacity-100"
            }`}
          />
          {/* ▲▲▲ UI-FIX: Centered Loading Spinner ▲▲▲ */}
        </div>
        {pdfDoc && (
          <PreviewToolbar
            scale={scale}
            setScale={setScale}
            onFit={fitToScreen}
            pageInfo={{ current: pageNum, total: numPages }}
            onPageChange={setPageNum}
          />
        )}
      </div>
    </div>
  );
};

const DocxPreviewModal = ({ file, content, onClose, onDownload }) => {
  const previewContainerRef = useRef(null);

  useEffect(() => {
    if (content && previewContainerRef.current) {
      const mammoth = globalThis.mammoth;
      if (mammoth && typeof mammoth.convertToHtml === "function") {
        mammoth
          .convertToHtml({ arrayBuffer: content })
          .then((result) => {
            if (previewContainerRef.current) {
              previewContainerRef.current.innerHTML = result.value;
            }
            if (result.messages && result.messages.length > 0) {
              console.warn("Mammoth messages:", result.messages);
            }
          })
          .catch((err) => {
            console.error("Mammoth conversion error:", err);
            if (previewContainerRef.current) {
              previewContainerRef.current.innerHTML = `<p class="text-red-500 font-bold">DOCXファイルのプレビューに失敗しました。</p><p class="mt-2 text-sm text-slate-600">${err.message}</p>`;
            }
          });
      } else {
        console.error("Mammoth.js library is not loaded.");
        if (previewContainerRef.current) {
          previewContainerRef.current.innerHTML =
            '<p class="text-red-500 font-bold">プレビュー用ライブラリ(Mammoth.js)の読み込みに失敗しました。</p>';
        }
      }
    }
  }, [content]);

  return (
    <div
      onClick={onClose}
      className="absolute inset-0 bg-slate-900 bg-opacity-75 flex items-center justify-center z-50 p-4 sm:p-8"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-lg shadow-xl w-full h-full max-w-6xl flex flex-col"
      >
        <header className="flex items-center justify-between p-3 border-b shrink-0">
          <span className="font-bold text-slate-700 truncate">{file.name}</span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onDownload(file)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-violet-500 text-white hover:bg-violet-600 transition-colors text-sm"
            >
              <Download size={16} />
              <span className="hidden sm:inline">ダウンロード</span>
            </button>
            <button
              onClick={onClose}
              className="p-2 sm:px-3 sm:py-1.5 rounded-lg bg-slate-200 text-slate-700 hover:bg-slate-300 transition-colors"
            >
              <X size={18} className="block sm:hidden" />
              <span className="hidden sm:inline">閉じる</span>
            </button>
          </div>
        </header>
        <div className="bg-slate-100 p-4 overflow-auto flex-1">
          <div
            ref={previewContainerRef}
            className="docx-preview-wrapper bg-white shadow-lg p-8 mx-auto"
          />
        </div>
      </div>
    </div>
  );
};

const XlsxPreviewModal = ({ file, workbook, onClose, onDownload }) => {
  const [activeSheetIndex, setActiveSheetIndex] = useState(0);
  const [sheetHtml, setSheetHtml] = useState("");

  useEffect(() => {
    if (!workbook) return;

    const { XLSX } = globalThis;
    const sheetNames = workbook.SheetNames || [];

    if (sheetNames.length === 0) {
      setSheetHtml(
        '<p class="p-4 text-slate-500">このExcelファイルには表示できるシートがありません。</p>'
      );
      return;
    }

    const viewIndex = 0; // 配列アクセス回避策
    const initialIndex = workbook.Workbook?.WBView?.[viewIndex]?.activeTab ?? 0;

    const validInitialIndex =
      initialIndex < sheetNames.length ? initialIndex : 0;

    const displaySheet = (index) => {
      const sheetName = sheetNames[index];
      const worksheet = workbook.Sheets[sheetName];
      const html = XLSX.utils.sheet_to_html(worksheet);
      setSheetHtml(html);
      setActiveSheetIndex(index);
    };

    displaySheet(validInitialIndex);
  }, [workbook]);

  const handleSheetChange = (index) => {
    const { XLSX } = globalThis;
    const sheetName = workbook.SheetNames[index];
    const worksheet = workbook.Sheets[sheetName];
    const html = XLSX.utils.sheet_to_html(worksheet);
    setSheetHtml(html);
    setActiveSheetIndex(index);
  };

  return (
    <div
      onClick={onClose}
      className="absolute inset-0 bg-slate-900 bg-opacity-75 flex items-center justify-center z-50 p-4 sm:p-8"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-lg shadow-xl w-full h-full max-w-6xl flex flex-col"
      >
        <header className="flex items-center justify-between p-3 border-b shrink-0">
          <span className="font-bold text-slate-700 truncate">{file.name}</span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onDownload(file)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-violet-500 text-white hover:bg-violet-600 transition-colors text-sm"
            >
              <Download size={16} />
              <span className="hidden sm:inline">ダウンロード</span>
            </button>
            <button
              onClick={onClose}
              className="p-2 sm:px-3 sm:py-1.5 rounded-lg bg-slate-200 text-slate-700 hover:bg-slate-300 transition-colors"
            >
              <X size={18} className="block sm:hidden" />
              <span className="hidden sm:inline">閉じる</span>
            </button>
          </div>
        </header>

        {workbook && workbook.SheetNames.length > 1 && (
          <div className="flex items-center flex-wrap gap-2 p-2 border-b bg-white">
            {workbook.SheetNames.map((name, index) => (
              <button
                key={name}
                onClick={() => handleSheetChange(index)}
                className={`px-3 py-1 text-sm rounded-md transition-colors ${
                  index === activeSheetIndex
                    ? "bg-violet-500 text-white font-semibold"
                    : "bg-slate-200 text-slate-700 hover:bg-slate-300"
                }`}
              >
                {name}
              </button>
            ))}
          </div>
        )}

        <div className="bg-slate-100 p-4 overflow-auto flex-1 sheetjs-preview">
          <style>{`
            .sheetjs-preview table {
              border-collapse: collapse;
              width: 100%;
              font-size: 12px;
            }
            .sheetjs-preview th, .sheetjs-preview td {
              border: 1px solid #ccc;
              padding: 4px 8px;
              text-align: left;
            }
            .sheetjs-preview thead th {
              background-color: #f2f2f2;
              position: sticky;
              top: 0;
            }
          `}</style>
          <div dangerouslySetInnerHTML={{ __html: sheetHtml }} />
        </div>
      </div>
    </div>
  );
};
//===2_2.SUB_COMPONENTS_Preview_Modals=End==

//===2_3.SUB_COMPONENTS_Input_Modals=Start==
const PasswordModal = ({ fileName, onConfirm, onCancel }) => {
  const [password, setPassword] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    onConfirm(password);
  };

  return (
    <div className="absolute inset-0 bg-slate-900 bg-opacity-50 flex items-center justify-center z-50">
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm"
      >
        <h3 className="text-lg font-bold text-slate-800 mb-2">
          パスワードが必要です
        </h3>
        <p className="text-slate-600 mb-4 text-sm truncate">
          ファイル: <span className="font-medium">{fileName}</span>
        </p>
        <div className="relative flex items-center mb-6">
          <Key
            size={18}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
          />
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="pl-10 w-full py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-400"
            placeholder="ZIPファイルのパスワード"
            autoFocus
          />
        </div>
        <div className="flex justify-end gap-4">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 rounded-lg bg-slate-200 text-slate-700 hover:bg-slate-300 transition-colors"
          >
            キャンセル
          </button>
          <button
            type="submit"
            className="px-4 py-2 rounded-lg bg-violet-500 text-white hover:bg-violet-600 transition-colors"
          >
            解凍
          </button>
        </div>
      </form>
    </div>
  );
};

const PromptModal = ({
  title,
  placeholder,
  initialValue = "",
  onConfirm,
  onCancel,
  confirmText = "決定",
}) => {
  const [value, setValue] = useState(initialValue);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (value) onConfirm(value);
  };

  return (
    <div className="absolute inset-0 bg-slate-900 bg-opacity-50 flex items-center justify-center z-50">
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm"
      >
        <h3 className="text-lg font-bold text-slate-800 mb-4">{title}</h3>
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="w-full py-2 px-3 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-400"
          placeholder={placeholder}
          autoFocus
          onFocus={(e) => e.target.select()}
        />
        <div className="flex justify-end gap-4 mt-6">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 rounded-lg bg-slate-200 text-slate-700 hover:bg-slate-300 transition-colors"
          >
            キャンセル
          </button>
          <button
            type="submit"
            disabled={!value}
            className="px-4 py-2 rounded-lg bg-violet-500 text-white hover:bg-violet-600 transition-colors disabled:bg-slate-300"
          >
            {confirmText}
          </button>
        </div>
      </form>
    </div>
  );
};

const ShareModal = ({ info, onClose }) => {
  const [copyButtonText, setCopyButtonText] =
    useState("クリップボードにコピー");
  const textToCopy = `【ファイル送付のご連絡】

ファイル名：${info.fileName}
パスワード：${info.password}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopyButtonText("コピーしました！");
      setTimeout(() => setCopyButtonText("クリップボードにコピー"), 2000);
    } catch (err) {
      console.error("クリップボードへのコピーに失敗しました:", err);
      setCopyButtonText("コピーに失敗");
      setTimeout(() => setCopyButtonText("クリップボードにコピー"), 2000);
    }
  };

  return (
    <div className="absolute inset-0 bg-slate-900 bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
        <h3 className="text-lg font-bold text-slate-800 mb-4">
          パスワード情報を共有
        </h3>
        <textarea
          readOnly
          value={textToCopy}
          className="w-full h-32 p-3 text-sm border border-slate-300 rounded-lg bg-slate-50 focus:outline-none focus:ring-2 focus:ring-violet-400 mb-6 resize-none"
        ></textarea>
        <p className="text-xs text-slate-500 mb-6 -mt-2">
          ※セキュリティのため、このパスワードはこのメッセージとは別の経路（例：電話、別のチャットツールなど）でお伝えいただくことを推奨します。
        </p>
        <div className="flex justify-end gap-2 sm:gap-4">
          <button
            onClick={handleCopy}
            className="px-3 sm:px-4 py-2 text-xs sm:text-sm rounded-lg bg-violet-500 text-white hover:bg-violet-600 transition-colors flex items-center gap-2"
          >
            <Clipboard size={16} />
            {copyButtonText}
          </button>
          <button
            onClick={onClose}
            className="px-3 sm:px-4 py-2 text-xs sm:text-sm rounded-lg bg-slate-200 text-slate-700 hover:bg-slate-300 transition-colors"
          >
            閉じる
          </button>
        </div>
      </div>
    </div>
  );
};
//===2_3.SUB_COMPONENTS_Input_Modals=End==

//===2_4.SUB_COMPONENTS_UI=Start==
const ContextMenu = ({ x, y, items, onClose }) => {
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  return (
    <div
      ref={menuRef}
      style={{ top: y, left: x }}
      className="absolute bg-white rounded-lg shadow-lg border border-slate-200 py-1.5 z-50 w-48 text-sm"
    >
      <ul>
        {items.map((item, index) => (
          <li key={index}>
            {item.separator ? (
              <hr className="my-1.5 border-slate-200" />
            ) : (
              <button
                onClick={() => {
                  item.action();
                  onClose();
                }}
                className="w-full flex items-center gap-3 px-3 py-1.5 text-left hover:bg-violet-50 transition-colors disabled:opacity-50"
                disabled={item.disabled}
              >
                {item.icon}
                <span>{item.label}</span>
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

const ActionButton = ({ icon, text, onClick, disabled = false }) => {
  const [isTouched, setIsTouched] = useState(false);
  const isTouchHandled = useRef(false);

  const handleTouchStart = () => {
    isTouchHandled.current = true;
    setIsTouched(true);
  };

  const handleTouchEnd = (e) => {
    setIsTouched(false);
    onClick(e);
    e.preventDefault();
  };

  const handleClick = (e) => {
    if (isTouchHandled.current) {
      isTouchHandled.current = false;
      return;
    }
    onClick(e);
  };

  const activeStyle = "bg-violet-50 border-violet-300 text-violet-600 scale-95";

  return (
    <button
      onClick={handleClick}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      disabled={disabled}
      className={`flex items-center gap-2 px-3 sm:px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 shadow-sm border
        ${
          disabled
            ? "bg-slate-200 text-slate-400 cursor-not-allowed border-transparent"
            : `bg-white text-slate-700 border-slate-300 hover:bg-violet-50 hover:border-violet-300 hover:text-violet-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-violet-400 active:scale-95 ${
                isTouched ? activeStyle : ""
              }`
        }`}
    >
      {icon}
      <span>{text}</span>
    </button>
  );
};

const ToolbarButton = ({ icon, text, onClick, disabled = false }) => {
  const [isTouched, setIsTouched] = useState(false);
  const isTouchHandled = useRef(false);

  const handleTouchStart = () => {
    isTouchHandled.current = true;
    setIsTouched(true);
  };

  const handleTouchEnd = (e) => {
    setIsTouched(false);
    onClick(e);
    e.preventDefault();
  };

  const handleClick = (e) => {
    if (isTouchHandled.current) {
      isTouchHandled.current = false;
      return;
    }
    onClick(e);
  };

  const activeStyle = "bg-violet-50 border-violet-300 text-violet-600 scale-95";

  return (
    <button
      onClick={handleClick}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      disabled={disabled}
      className={`flex flex-col items-center justify-center gap-1 p-1 sm:p-2 rounded-lg transition-all duration-200 w-14 sm:w-20 h-16 border
        ${
          disabled
            ? "bg-slate-100 text-slate-400 cursor-not-allowed border-transparent"
            : `bg-white text-slate-600 border-slate-200 hover:bg-violet-50 hover:border-violet-300 hover:text-violet-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-violet-400 active:scale-95 ${
                isTouched ? activeStyle : ""
              }`
        }`}
    >
      {icon}
      <span className="text-xs font-medium whitespace-nowrap">{text}</span>
    </button>
  );
};
//===2_4.SUB_COMPONENTS_UI=End==

//===2_5.SUB_COMPONENTS_Views=Start==
const FolderTreeView = ({
  tree,
  onSelect,
  selectedPath,
  path = "",
  treeDropTarget,
  setTreeDropTarget,
  onDropIntoTreeFolder,
  onContextMenu,
  files,
  clipboard,
}) => {
  const entries = Object.entries(tree).sort(([a], [b]) =>
    a.localeCompare(b, "ja", { numeric: true })
  );

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleDragEnter = (e, currentPath) => {
    e.preventDefault();
    e.stopPropagation();
    setTreeDropTarget(currentPath);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setTreeDropTarget(null);
    }
  };
  const handleDrop = (e, currentPath) => {
    e.preventDefault();
    e.stopPropagation();
    if (treeDropTarget === currentPath) {
      onDropIntoTreeFolder(e, currentPath);
    }
    setTreeDropTarget(null);
  };

  const FolderItem = ({ name, children, currentPath }) => {
    const longPressTimerId = useRef(null);
    const isLongPressHandled = useRef(false);

    const handleTouchStart = (e) => {
      isLongPressHandled.current = false;
      const eventForLongPress = {
        clientX: e.touches.clientX,
        clientY: e.touches.clientY,
        preventDefault: () => e.preventDefault(),
        stopPropagation: () => e.stopPropagation(),
      };

      longPressTimerId.current = setTimeout(() => {
        onContextMenu(eventForLongPress, currentPath);
        isLongPressHandled.current = true;
      }, 400);
    };

    const handleTouchEnd = (e) => {
      clearTimeout(longPressTimerId.current);
      if (isLongPressHandled.current) {
        e.preventDefault();
        return;
      }
      onSelect(currentPath);
    };

    const handleMouseDown = (e) => {
      if (e.button !== 0) return;
      onSelect(currentPath);
    };

    const handleContextMenu = (e) => {
      e.preventDefault();
      clearTimeout(longPressTimerId.current);
      onContextMenu(e, currentPath);
    };

    const fileObject = files.find((f) => f.path === currentPath);
    const isCut =
      clipboard.type === "cut" &&
      fileObject &&
      clipboard.ids.has(fileObject.id);

    return (
      <li className="ml-4">
        <div
          onMouseDown={handleMouseDown}
          onContextMenu={handleContextMenu}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          data-is-item="true"
          onDragOver={handleDragOver}
          onDragEnter={(e) => handleDragEnter(e, currentPath)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, currentPath)}
          className={`long-press-target flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors
            ${
              selectedPath === currentPath
                ? "bg-violet-100 text-violet-700 font-bold"
                : "hover:bg-sky-100"
            }
            ${treeDropTarget === currentPath ? "bg-green-200" : ""}
            ${isCut ? "opacity-50" : ""}`}
        >
          <Folder size={18} className="pointer-events-none" />
          <span className="pointer-events-none">{name}</span>
        </div>
        {Object.keys(children).length > 0 && (
          <ul>
            <FolderTreeView
              tree={children}
              onSelect={onSelect}
              selectedPath={selectedPath}
              path={currentPath}
              treeDropTarget={treeDropTarget}
              setTreeDropTarget={setTreeDropTarget}
              onDropIntoTreeFolder={onDropIntoTreeFolder}
              onContextMenu={onContextMenu}
              files={files}
              clipboard={clipboard}
            />
          </ul>
        )}
      </li>
    );
  };

  return (
    <>
      {entries.map(([name, children]) => {
        const currentPath = `${path}${name}/`;
        return (
          <FolderItem
            key={currentPath}
            name={name}
            children={children}
            currentPath={currentPath}
          />
        );
      })}
    </>
  );
};

const FileIconWithPreview = ({ isPreviewable }) => {
  const StatusIcon = isPreviewable ? Eye : EyeOff;
  const statusColor = isPreviewable ? "text-violet-500" : "text-slate-400";
  const fileColor = isPreviewable ? "text-violet-500" : "text-slate-500";

  return (
    <div className="relative w-6 h-6 flex items-center justify-center">
      <FileIcon size={24} className={fileColor} />
      <div className="absolute -bottom-0.5 -right-0.5 flex items-center justify-center bg-white p-0.5 rounded-full">
        <StatusIcon size={12} className={statusColor} />
      </div>
    </div>
  );
};

const FileItem = ({
  file,
  onDownload,
  isSelected,
  onFileSelect,
  dropTargetId,
  setDropTargetId,
  onDropOnItem,
  onContextMenu,
  isSelectionMode,
  clipboard,
}) => {
  const isPreviewable = (file) => {
    if (file.isDir) return false;

    // ▼▼▼ REFACTOR: Centralized Size Limit Logic ▼▼▼
    const sizeLimits = {
      high: 30 * 1024 * 1024, // 30MB
      default: 5 * 1024 * 1024, // 5MB
    };
    const highLimitExtensions = ["psd", "pdf", "ai"];
    const lowerCaseName = file.name.toLowerCase();
    const extension = lowerCaseName.split(".").pop();
    const sizeLimit = highLimitExtensions.includes(extension)
      ? sizeLimits.high
      : sizeLimits.default;

    if (file.size > sizeLimit) return false;
    // ▲▲▲ REFACTOR: Centralized Size Limit Logic ▲▲▲

    const imageExtensions = ["jpg", "jpeg", "png", "gif", "webp", "svg"];
    const textExtensions = ["txt", "md", "json", "js", "css", "html", "xml"];
    const tableExtensions = ["csv", "tsv"];
    const officeExtensions = ["docx", "xlsx", "xls"];
    const psdExtension = "psd";

    if (imageExtensions.some((ext) => lowerCaseName.endsWith(`.${ext}`)))
      return true;
    if (textExtensions.some((ext) => lowerCaseName.endsWith(`.${ext}`)))
      return true;
    if (tableExtensions.some((ext) => lowerCaseName.endsWith(`.${ext}`)))
      return true;
    if (officeExtensions.some((ext) => lowerCaseName.endsWith(`.${ext}`)))
      return true;
    if (lowerCaseName.endsWith(`.${psdExtension}`)) return true;
    if (lowerCaseName.endsWith(".pdf")) return true;
    if (lowerCaseName.endsWith(".ai") && file.isPdfCompatible) return true;

    return false;
  };

  const getIcon = () => {
    if (file.isDir) {
      return <Folder className="text-yellow-500" />;
    }

    const lowerCaseName = file.name.toLowerCase();
    const potentiallyPreviewableExtensions = [
      "jpg",
      "jpeg",
      "png",
      "gif",
      "webp",
      "svg", // images
      "txt",
      "md",
      "json",
      "js",
      "css",
      "html",
      "xml", // text
      "csv",
      "tsv", // tables
      "docx",
      "xlsx",
      "xls", // office
      "pdf",
      "ai",
      "psd", // special docs
    ];

    const isPotentiallyPreviewable = potentiallyPreviewableExtensions.some(
      (ext) => lowerCaseName.endsWith(`.${ext}`)
    );

    if (isPotentiallyPreviewable) {
      const isActuallyPreviewable = isPreviewable(file);
      return <FileIconWithPreview isPreviewable={isActuallyPreviewable} />;
    }

    return <FileIcon className="text-slate-500" />;
  };

  const getFileType = (file) => {
    if (file.isDir) return "フォルダ";
    const parts = file.name.split(".");
    if (parts.length > 1) {
      const ext = parts.pop();
      return `${ext.toUpperCase()} ファイル`;
    }
    return "ファイル";
  };
  const formatSize = (bytes) => {
    if (file.isDir) return "--";
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };
  const formatDate = (date) =>
    date ? new Intl.DateTimeFormat("ja-JP").format(date) : "";

  const longPressTimerId = useRef(null);
  const isLongPressHandled = useRef(false);

  const handleTouchStart = (e) => {
    e.stopPropagation();
    isLongPressHandled.current = false;
    const eventForLongPress = {
      clientX: e.touches.clientX,
      clientY: e.touches.clientY,
      preventDefault: () => e.preventDefault(),
      stopPropagation: () => e.stopPropagation(),
    };

    longPressTimerId.current = setTimeout(() => {
      onContextMenu(eventForLongPress, file.id);
      isLongPressHandled.current = true;
    }, 400);
  };

  const handleTouchEnd = (e) => {
    e.stopPropagation();
    clearTimeout(longPressTimerId.current);
    if (isLongPressHandled.current) {
      e.preventDefault();
      return;
    }
    e.preventDefault();
    onFileSelect(e, file.id);
  };

  const handleMouseDown = (e) => {
    e.stopPropagation();
    if (e.button !== 0) return;
    onFileSelect(e, file.id);
  };

  const handleContextMenu = (e) => {
    e.stopPropagation();
    e.preventDefault();
    clearTimeout(longPressTimerId.current);
    onContextMenu(e, file.id);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const targetCell = e.target.closest("div");
    if (file.isDir && targetCell) {
      setDropTargetId(file.id);
    } else {
      setDropTargetId(null);
    }
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDropTargetId(null);
    }
  };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const targetCell = e.target.closest("div");
    onDropOnItem(e, file, !!targetCell);
    setDropTargetId(null);
  };

  const isCut = clipboard.type === "cut" && clipboard.ids.has(file.id);

  return (
    <div
      onMouseDown={handleMouseDown}
      onContextMenu={handleContextMenu}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      data-is-item="true"
      onDoubleClick={() => !isSelectionMode && onDownload(file)}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={`long-press-target flex items-center border-b border-sky-100 select-none w-full
        ${!isSelectionMode ? "cursor-pointer" : ""}
        ${isSelected ? "bg-violet-200 hover:bg-violet-300" : "hover:bg-sky-50"}
        ${dropTargetId === file.id ? "bg-green-200" : ""}
        ${isCut ? "opacity-50" : ""}`}
    >
      {isSelectionMode && (
        <div className="p-3 pointer-events-none">
          <input
            type="checkbox"
            checked={isSelected}
            readOnly
            className="form-checkbox h-5 w-5 rounded text-violet-600 transition duration-150 ease-in-out pointer-events-none"
          />
        </div>
      )}
      <div className="p-3 flex items-center gap-3 flex-1 min-w-0 pointer-events-none">
        {getIcon()}
        <span className="break-all truncate">{file.name}</span>
      </div>
      <div className="hidden md:flex p-3 text-slate-600 w-40 items-center pointer-events-none">
        {getFileType(file)}
      </div>
      <div className="flex p-3 text-slate-600 w-24 justify-end items-center pointer-events-none">
        {formatSize(file.size)}
      </div>
      <div className="hidden md:flex p-3 text-slate-600 w-32 justify-end items-center pointer-events-none">
        {formatDate(file.modified)}
      </div>
    </div>
  );
};
//===2_5.SUB_COMPONENTS_Views=End==

//===3.MAIN_APP_COMPONENT_AND_STATE=Start==
export default function App() {
  const [files, setFiles] = useState([]);
  const [history, setHistory] = useState({ snapshots: [[]], current: 0 });
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [archiveName, setArchiveName] = useState(null);
  const [currentPath, setCurrentPath] = useState("");
  const [folderTree, setFolderTree] = useState({});
  const [compressionLevel, setCompressionLevel] = useState(5);
  const [isResetModalVisible, setIsResetModalVisible] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: "name", order: "asc" });
  const [previewData, setPreviewData] = useState(null);
  const [password, setPassword] = useState("");
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [unzipRequest, setUnzipRequest] = useState(null);
  const [decryptionPassword, setDecryptionPassword] = useState("");
  const [selectedFileIds, setSelectedFileIds] = useState(new Set());
  const [lastClickedId, setLastClickedId] = useState(null);
  const [replaceConfirm, setReplaceConfirm] = useState(null);
  const [isModified, setIsModified] = useState(false);
  const [dropTargetId, setDropTargetId] = useState(null);
  const [treeDropTarget, setTreeDropTarget] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [prompt, setPrompt] = useState(null);
  const [compressionProgress, setCompressionProgress] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [clipboard, setClipboard] = useState({ type: null, ids: new Set() });
  const [isShareModalVisible, setIsShareModalVisible] = useState(false);
  const [lastCompressedInfo, setLastCompressedInfo] = useState(null);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [pdfPreviewData, setPdfPreviewData] = useState(null);
  const [csvPreviewData, setCsvPreviewData] = useState(null);
  const [docxPreviewData, setDocxPreviewData] = useState(null);
  const [xlsxPreviewData, setXlsxPreviewData] = useState(null);
  const [sessionRestoreRequest, setSessionRestoreRequest] = useState(null);
  const [splitSize, setSplitSize] = useState(25);
  const [isSplitEnabled, setIsSplitEnabled] = useState(false);
  const [splitRestoreRequest, setSplitRestoreRequest] = useState(null);
  const [splitFileNamePrompt, setSplitFileNamePrompt] = useState(null);
  const [splitPromptInput, setSplitPromptInput] = useState("");
  // ▼▼▼ DOWNLOAD-LIST-MODAL ▼▼▼
  const [downloadList, setDownloadList] = useState(null);
  // ▲▲▲ DOWNLOAD-LIST-MODAL ▲▲▲
  const dragCounter = useRef(0);
  const fileInputRef = useRef(null);
  const navigationLock = useRef(false);
  const appContainerRef = useRef(null);
  const addTargetPathRef = useRef("");

  useEffect(() => {
    const touchCheck = "ontouchstart" in window || navigator.maxTouchPoints > 0;
    setIsTouchDevice(touchCheck);
  }, []);

  // ▼▼▼ FEATURE-SESSION: Core Logic ▼▼▼
  useEffect(() => {
    // アプリ起動時に一度だけセッションを確認
    const checkForSession = async () => {
      const sessionData = await getSession();
      if (sessionData && sessionData.files && sessionData.files.length > 0) {
        setSessionRestoreRequest(sessionData);
      }
    };
    checkForSession();
  }, []);

  useEffect(() => {
    // 状態が変更されるたびにセッションを自動保存
    const saveCurrentSession = async () => {
      if (files.length === 0 && !archiveName) {
        // 完全にクリアされた状態なら保存データも削除
        await deleteSession();
        return;
      }
      const combinedPassword = decryptionPassword || password;
      const passwordHash = await hashPassword(combinedPassword);

      // IndexedDBに保存する前に、`rawFile`プロパティ（関数を含む可能性がある）を削除する
      const filesForSession = files.map(
        ({ rawFile, ...restOfFile }) => restOfFile
      );

      const sessionData = {
        files: filesForSession, // rawFileが削除された配列を使用
        archiveName,
        passwordHash,
        timestamp: new Date().toISOString(),
      };
      await setSession(sessionData);
    };

    // history.currentが0でfilesが空の場合（初期状態やリセット直後）は保存しない
    if (history.current === 0 && files.length === 0) {
      return;
    }

    saveCurrentSession();
  }, [files, archiveName, password, decryptionPassword, history]);
  // ▲▲▲ FEATURE-SESSION: Core Logic ▲▲▲

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (prompt || contextMenu) {
        return;
      }
      if (e.key === "Delete" && selectedFileIds.size > 0) {
        handleDeleteFiles();
      } else if (e.key === "F2" && selectedFileIds.size === 1) {
        handleRename();
      } else if (e.key === "Escape") {
        if (clipboard.type === "cut") {
          setClipboard({ type: null, ids: new Set() });
        }
        if (isSelectionMode) {
          setIsSelectionMode(false);
          handleClearSelection();
        }
      } else if ((e.ctrlKey || e.metaKey) && e.key === "z") {
        e.preventDefault();
        handleUndo();
      } else if ((e.ctrlKey || e.metaKey) && e.key === "y") {
        e.preventDefault();
        handleRedo();
      } else if ((e.ctrlKey || e.metaKey) && e.key === "c") {
        handleCopy();
      } else if ((e.ctrlKey || e.metaKey) && e.key === "x") {
        handleCut();
      } else if ((e.ctrlKey || e.metaKey) && e.key === "v") {
        handlePaste(currentPath);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    selectedFileIds,
    files,
    prompt,
    contextMenu,
    isSelectionMode,
    currentPath,
    clipboard,
    history,
  ]);

  useEffect(() => {
    const setAppHeight = () => {
      if (appContainerRef.current) {
        appContainerRef.current.style.height = `${window.innerHeight}px`;
      }
    };
    setAppHeight();
    window.addEventListener("resize", setAppHeight);
    return () => window.removeEventListener("resize", setAppHeight);
  }, []);
  //===3.MAIN_APP_COMPONENT_AND_STATE=End==

  //===4_1.HANDLERS_Utils=Start==
  // ▼▼▼ FEATURE-SESSION: IndexedDB & Crypto Helpers ▼▼▼
  const DB_NAME = "kyutto-file-db";
  const STORE_NAME = "session-store";

  const getDB = () => {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, 1);
      request.onupgradeneeded = () => {
        request.result.createObjectStore(STORE_NAME);
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = (e) => reject(e.target.error);
    });
  };

  const setSession = async (data) => {
    const db = await getDB();
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).put(data, "current-session");
    return tx.done;
  };

  const getSession = async () => {
    const db = await getDB();
    return new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, "readonly");
      const request = tx.objectStore(STORE_NAME).get("current-session");
      tx.oncomplete = () => resolve(request.result);
    });
  };

  const deleteSession = async () => {
    const db = await getDB();
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).delete("current-session");
    return tx.done;
  };

  const hashPassword = async (str) => {
    if (!str) return null;
    try {
      const encoder = new TextEncoder();
      const data = encoder.encode(str);
      const hashBuffer = await crypto.subtle.digest("SHA-256", data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const hashHex = hashArray
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
      return hashHex;
    } catch (error) {
      console.error("Password hashing failed:", error);
      // 暗号化APIが利用できない環境（非HTTPSなど）のためのフォールバック
      return `unhashed:${str}`;
    }
  };
  // ▲▲▲ FEATURE-SESSION: IndexedDB & Crypto Helpers ▲▲▲

  const getMimeTypeFromFilename = (filename) => {
    const extension = (filename || "").split(".").pop().toLowerCase();
    const mimeTypes = {
      jpg: "image/jpeg",
      jpeg: "image/jpeg",
      png: "image/png",
      gif: "image/gif",
      webp: "image/webp",
      svg: "image/svg+xml",
      txt: "text/plain",
      md: "text/markdown",
      json: "application/json",
      js: "application/javascript",
      css: "text/css",
      html: "text/html",
      xml: "application/xml",
    };
    return mimeTypes[extension] || "application/octet-stream";
  };
  //===4_1.HANDLERS_Utils=End==

  //===4_2.HANDLERS_FileSystem_Actions=Start==
  const handleNewClick = async () => {
    // ▼▼▼ FEATURE-SESSION: Check for saved data on new click ▼▼▼
    const savedSession = await getSession();
    if (savedSession) {
      // 保存データがある場合は、それを削除するか確認
      setIsResetModalVisible(true);
    } else if (files.length > 0) {
      // ファイルはあるが保存データはない場合
      setIsResetModalVisible(true);
    } else {
      // 何もない場合
      confirmReset();
    }
    // ▲▲▲ FEATURE-SESSION: Check for saved data on new click ▲▲▲
  };
  const confirmReset = async () => {
    // ▼▼▼ FEATURE-SESSION: Clear saved data on reset ▼▼▼
    await deleteSession();
    // ▲▲▲ FEATURE-SESSION: Clear saved data on reset ▲▲▲

    const initialFiles = [];
    setFiles(initialFiles);
    setArchiveName(null);
    updateFolderTree(initialFiles);
    setCurrentPath("");
    setIsResetModalVisible(false);
    setDecryptionPassword("");
    setIsModified(false);
    setHistory({ snapshots: [initialFiles], current: 0 });
    setLastCompressedInfo(null);
    setPassword("");
  };
  const cancelReset = () => setIsResetModalVisible(false);

  const handleFileAddClick = (path) => {
    const targetPath = path ?? currentPath;
    addTargetPathRef.current = targetPath;
    fileInputRef.current?.click();
  };

  const handleFileAddChange = async (e) => {
    if (!e.target.files || e.target.files.length === 0) {
      return;
    }
    const fileList = e.target.files;
    const entries = await getEntriesFromFileList(fileList);
    const targetPath = addTargetPathRef.current;
    handleAppendFiles(entries, targetPath, false);
    e.target.value = "";
  };

  const handleNewFolder = () => {
    const targetPath = contextMenu ? contextMenu.path : currentPath;
    setPrompt({
      type: "newFolder",
      onConfirm: (name) => confirmNewFolder(name, targetPath),
    });
  };

  const confirmNewFolder = (name, basePath) => {
    const newPath = `${basePath}${name}/`;
    const nameExists = files.some(
      (f) => f.isDir && f.path.toLowerCase() === newPath.toLowerCase()
    );

    if (nameExists) {
      alert(`「${name}」という名前のフォルダは既に存在します。`);
      return;
    }
    const newFolder = {
      id: Date.now(),
      name: name,
      path: newPath,
      size: 0,
      modified: new Date(),
      rawFile: null,
      isDir: true,
    };
    const newFiles = [...files, newFolder];
    recordHistory(newFiles);
    setPrompt(null);
  };

  const handleRename = () => {
    if (selectedFileIds.size !== 1) return;
    const targetId = selectedFileIds.values().next().value;
    const fileToRename = files.find((f) => f.id === targetId);
    if (!fileToRename) return;
    setPrompt({
      type: "rename",
      targetId: targetId,
      initialValue: fileToRename.name,
      onConfirm: (newName) => confirmRename(targetId, newName),
    });
  };

  const confirmRename = (targetId, newName) => {
    const targetFile = files.find((f) => f.id === targetId);
    if (!targetFile || targetFile.name === newName) {
      setPrompt(null);
      return;
    }
    const parentPath = targetFile.path.substring(
      0,
      targetFile.path.lastIndexOf(targetFile.name)
    );
    const newPath = `${parentPath}${newName}${targetFile.isDir ? "/" : ""}`;
    const nameExists = files.some(
      (f) => f.id !== targetId && f.path === newPath
    );
    if (nameExists) {
      alert(`同じ名前のアイテムが既に存在します。`);
      return;
    }
    const newFiles = files.map((file) => {
      if (file.id === targetId) {
        return { ...file, name: newName, path: newPath };
      }
      if (targetFile.isDir && file.path.startsWith(targetFile.path)) {
        return { ...file, path: file.path.replace(targetFile.path, newPath) };
      }
      return file;
    });
    recordHistory(newFiles);
    setPrompt(null);
  };

  const updateFolderTree = (fileList) => {
    const newTree = {};
    for (const file of fileList) {
      if (file.isDir) {
        const segments = file.path.replace(/\/$/, "").split("/");
        let currentLevel = newTree;
        for (const segment of segments) {
          if (segment) {
            currentLevel = currentLevel[segment] = currentLevel[segment] || {};
          }
        }
      }
    }
    setFolderTree(newTree);
  };

  const handleGeneratePassword = () => {
    const length = 12;
    const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const lower = "abcdefghijklmnopqrstuvwxyz";
    const numbers = "0123456789";
    const symbols = "!@#$%_-"; // 一般的で安全な記号セット
    const allChars = upper + lower + numbers + symbols;

    // 各種類から最低1文字を保証
    let guaranteedChars = [
      upper[Math.floor(Math.random() * upper.length)],
      lower[Math.floor(Math.random() * lower.length)],
      numbers[Math.floor(Math.random() * numbers.length)],
      symbols[Math.floor(Math.random() * symbols.length)],
    ];

    // 残りの文字をランダムに選択
    for (let i = guaranteedChars.length; i < length; i++) {
      guaranteedChars.push(
        allChars[Math.floor(Math.random() * allChars.length)]
      );
    }

    // 配列をシャッフルして文字の順序をランダム化 (Fisher-Yates shuffle)
    for (let i = guaranteedChars.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [guaranteedChars[i], guaranteedChars[j]] = [
        guaranteedChars[j],
        guaranteedChars[i],
      ];
    }

    const newPassword = guaranteedChars.join("");
    setPassword(newPassword);
    setLastCompressedInfo(null); // パスワードが変更されたので共有情報をクリア
  };
  //===4_2.HANDLERS_FileSystem_Actions=End==

  //===4_3.HANDLERS_DragDrop=Start==
  const handleAppendFiles = useCallback(
    async (entries, basePath, isFolderTarget = false) => {
      // ▼▼▼ FEATURE-SPLIT-ZIP (zip.js version) ▼▼▼
      if (entries.length === 1) {
        const entryIndex = 0; // 配列アクセス回避策
        const file = entries[entryIndex].getRawFile();
        const lowerCaseName = file.name.toLowerCase();

        // 分割ZIPファイル（.zip.001）を検出
        if (/\.zip\.\d{3}$/.test(lowerCaseName)) {
          console.log("handleAppendFiles: Split ZIP file detected:", file.name);
          setSplitRestoreRequest(file);
          return;
        }
      }
      // ▲▲▲ FEATURE-SPLIT-ZIP (zip.js version) ▲▲▲

      const entryIndex = 0; // 配列アクセス回避策
      const isSingleZip =
        entries.length === 1 &&
        entries[entryIndex].filename.toLowerCase().endsWith(".zip");

      if (isSingleZip && !isFolderTarget) {
        const zipFile = entries[entryIndex].getRawFile();
        if (!zipFile) {
          console.error("Could not retrieve file for single ZIP open.");
          return;
        }

        if (!isModified) {
          handleDecompress(zipFile, password);
        } else {
          setReplaceConfirm({ file: zipFile, path: basePath });
        }
      } else {
        if (basePath) {
          entries.forEach((entry) => {
            entry.filename = `${basePath}${entry.filename}`;
          });
        }
        if (files.length === 0) {
          processUnzippedData(entries, "ファイル一覧", false);
        } else {
          processUnzippedData(entries, archiveName || "ファイル一覧", true);
        }
      }
    },
    [isModified, password, files, archiveName]
  );

  const handleDrop = useCallback(
    async (e) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current = 0;
      setIsDraggingOver(false);
      setTreeDropTarget(null);
      if (e.dataTransfer.files.length === 0) return;
      const entries = await getEntriesFromDataTransfer(e.dataTransfer.items);
      if (entries.length === 0) return;
      handleAppendFiles(entries, currentPath, false);
    },
    [handleAppendFiles, currentPath]
  );
  const handleDropOnItem = useCallback(
    async (e, targetFile, isNameCell) => {
      e.preventDefault();
      e.stopPropagation();
      const entries = await getEntriesFromDataTransfer(e.dataTransfer.items);
      if (entries.length === 0) return;
      if (targetFile.isDir && isNameCell) {
        handleAppendFiles(entries, targetFile.path, true);
      } else {
        const parentPath = currentPath;
        handleAppendFiles(entries, parentPath, false);
      }
    },
    [currentPath, handleAppendFiles]
  );
  const handleDropIntoTreeFolder = useCallback(
    async (e, targetPath) => {
      e.preventDefault();
      e.stopPropagation();
      const entries = await getEntriesFromDataTransfer(e.dataTransfer.items);
      if (entries.length === 0) return;
      handleAppendFiles(entries, targetPath, true);
    },
    [handleAppendFiles]
  );
  const getEntriesFromFileList = (files) => {
    const entries = [];
    for (const file of files) {
      entries.push({
        filename: file.name,
        directory: false,
        uncompressedSize: file.size,
        lastModDate: new Date(file.lastModified),
        getRawFile: () => file,
      });
    }
    return Promise.resolve(entries);
  };
  const getEntriesFromDataTransfer = (items) => {
    return new Promise((resolve) => {
      const entries = [];
      const itemsLeft = items.length;
      let itemsProcessed = 0;
      const processEntry = (entry, path) => {
        return new Promise(async (resolveEntry) => {
          if (entry.isFile) {
            entry.file((file) => {
              entries.push({
                filename: path + file.name,
                directory: false,
                uncompressedSize: file.size,
                lastModDate: new Date(file.lastModified),
                getRawFile: () => file,
              });
              resolveEntry();
            });
          } else if (entry.isDirectory) {
            entries.push({
              filename: path + entry.name + "/",
              directory: true,
              uncompressedSize: 0,
              lastModDate: new Date(),
              getRawFile: () => null,
            });
            const dirReader = entry.createReader();
            const readEntries = () => {
              dirReader.readEntries(async (dirEntries) => {
                if (dirEntries.length > 0) {
                  const promises = dirEntries.map((e) =>
                    processEntry(e, path + entry.name + "/")
                  );
                  await Promise.all(promises);
                  readEntries();
                } else {
                  resolveEntry();
                }
              });
            };
            readEntries();
          }
        });
      };
      if (itemsLeft === 0) {
        resolve([]);
        return;
      }
      for (const item of items) {
        const entry = item.webkitGetAsEntry();
        if (entry) {
          processEntry(entry, "").then(() => {
            itemsProcessed++;
            if (itemsProcessed === itemsLeft) resolve(entries);
          });
        } else {
          itemsProcessed++;
          if (itemsProcessed === itemsLeft) resolve(entries);
        }
      }
    });
  };

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (dragCounter.current === 1) setIsDraggingOver(true);
  }, []);
  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    setDropTargetId(null);
    setTreeDropTarget(null);
    if (dragCounter.current === 0) setIsDraggingOver(false);
  }, []);
  //===4_3.HANDLERS_DragDrop=End==

  //===4_4.HANDLERS_ZipOperations=Start==
  const processUnzippedData = async (
    entries,
    archiveName,
    shouldAppend = false
  ) => {
    let baseFiles = shouldAppend ? [...files] : [];
    let idCounter = Date.now() + Math.random();
    const addedPaths = new Set(baseFiles.map((f) => f.path));

    const checkAiCompatibility = async (fileBlob) => {
      if (!fileBlob || typeof fileBlob.slice !== "function") return false;
      try {
        const slice = fileBlob.slice(0, 1024);
        const text = await slice.text();
        return text.includes("%PDF-");
      } catch (e) {
        console.error("Could not read AI file for compatibility check:", e);
        return false;
      }
    };

    for (const entry of entries) {
      const normalizedPath = entry.filename.startsWith("/")
        ? entry.filename.substring(1)
        : entry.filename;
      if (!normalizedPath || addedPaths.has(normalizedPath)) continue;
      const isDir = entry.directory;
      const segments = normalizedPath.replace(/\/$/, "").split("/");
      let currentCumulativePath = "";
      for (let i = 0; i < segments.length - (isDir ? 0 : 1); i++) {
        const segment = segments[i];
        if (!segment) continue;
        currentCumulativePath += segment + "/";
        if (!addedPaths.has(currentCumulativePath)) {
          baseFiles.push({
            id: idCounter++,
            name: segment,
            path: currentCumulativePath,
            size: 0,
            modified: entry.lastModDate,
            rawFile: null,
            isDir: true,
          });
          addedPaths.add(currentCumulativePath);
        }
      }
      if (!isDir) {
        const name = segments[segments.length - 1];
        if (name) {
          const rawFile = entry.getRawFile ? entry.getRawFile() : entry;
          const isAi = name.toLowerCase().endsWith(".ai");
          const isPdfCompatible = isAi
            ? await checkAiCompatibility(rawFile)
            : undefined;

          baseFiles.push({
            id: idCounter++,
            name: name,
            path: normalizedPath,
            size: entry.uncompressedSize,
            modified: entry.lastModDate,
            rawFile: rawFile,
            isDir: false,
            isPdfCompatible: isPdfCompatible,
          });
          addedPaths.add(normalizedPath);
        }
      }
    }

    setFiles(baseFiles);
    updateFolderTree(baseFiles);
    setArchiveName(archiveName);

    if (!shouldAppend) {
      setHistory({ snapshots: [baseFiles], current: 0 });
      setCurrentPath("");
      setSelectedFileIds(new Set());
    } else if (entries.length > 0) {
      recordHistory(baseFiles);
    }

    if (entries.length > 0) setIsModified(true);
  };

  const handleCombineAndDecompress = async (splitFiles) => {
    setCompressionProgress({
      title: "分割ファイルを結合中...",
      message: "しばらくお待ちください...",
      progress: 50,
    });
    try {
      const mergedBlob = new Blob(splitFiles);
      const { zip } = globalThis;
      const reader = new zip.ZipReader(new zip.BlobReader(mergedBlob));
      const entries = await reader.getEntries();
      await confirmReset();

      // ▼▼▼ BUG FIX: Correctly access the first file name ▼▼▼
      const firstFileIndex = 0; // 配列アクセス回避策
      const firstFileName = splitFiles[firstFileIndex].name;
      // ▲▲▲ BUG FIX: Correctly access the first file name ▲▲▲
      const archiveBaseName = firstFileName.replace(/\.zip\.\d{3}$/i, "");
      processUnzippedData(entries, `${archiveBaseName}.zip`, false);
      setIsModified(false);
      await reader.close();
    } catch (error) {
      console.error("Split ZIP combine error:", error);
      alert("分割ZIPファイルの結合・展開に失敗しました。");
    } finally {
      setCompressionProgress(null);
      setSplitRestoreRequest(null);
    }
  };

  const handleDecompress = async (file, password) => {
    const { zip } = globalThis;
    if (!zip) {
      alert("ZIPライブラリの読み込みに失敗しました。");
      return;
    }
    const reader = new zip.ZipReader(new zip.BlobReader(file));
    try {
      await confirmReset();
      const entries = await reader.getEntries();
      const firstEncryptedFile = entries.find(
        (entry) => entry.encrypted && !entry.directory
      );
      if (firstEncryptedFile) {
        if (!password) {
          setUnzipRequest(file);
          await reader.close();
          return;
        }
        try {
          await firstEncryptedFile.getData(new zip.Writer(), {
            password,
            useWebWorkers: true,
            length: 1,
          });
        } catch (validationError) {
          setUnzipRequest(file);
          await reader.close();
          return;
        }
      }
      processUnzippedData(entries, file.name, false);
      setDecryptionPassword(password || "");
      setIsModified(false);
      await reader.close();
    } catch (error) {
      console.error("解凍エラー:", error);
      alert(`ファイルの解凍に失敗しました: ${error.message}`);
      await reader.close();
    }
  };
  const handleUnzipWithPassword = (password) => {
    if (unzipRequest) {
      handleDecompress(unzipRequest, password);
    }
    setUnzipRequest(null);
  };

  const executeCompression = async (finalArchiveName) => {
    setCompressionProgress({
      title: "圧縮の準備中...",
      message: "しばらくお待ちください...",
      progress: 0,
    });

    const { zip } = globalThis;
    if (!zip) {
      alert("ZIPライブラリの読み込みに失敗しました。");
      setCompressionProgress(null);
      return;
    }

    try {
      const filesToCompress = files.filter((f) => !f.isDir && f.rawFile);
      const blobWriter = new zip.BlobWriter("application/zip");
      const zipWriterOptions = {
        ...(password && { password, encryptionStrength: 3 }),
        level: compressionLevel,
        useWebWorkers: true,
      };
      const zipWriter = new zip.ZipWriter(blobWriter, zipWriterOptions);

      const totalFiles = filesToCompress.length;
      let filesProcessed = 0;

      for (const file of files) {
        if (file.isDir) {
          await zipWriter.add(file.path, null);
        }
      }

      for (const file of filesToCompress) {
        let fileData;
        if (file.rawFile instanceof Blob) {
          fileData = file.rawFile;
        } else {
          fileData = await file.rawFile.getData(new zip.BlobWriter(), {
            password: decryptionPassword,
            useWebWorkers: true,
          });
        }

        const fileAddOptions = {
          onprogress: (current, total) => {
            const fileProgress = total > 0 ? (current / total) * 100 : 0;
            const overallProgress =
              (filesProcessed * 100 + fileProgress) / totalFiles;
            setCompressionProgress((prev) => ({
              ...prev,
              title: "圧縮中...",
              message: `ファイル (${filesProcessed + 1}/${totalFiles}): ${
                file.name
              }`,
              progress: Math.floor(overallProgress),
            }));
          },
        };

        await zipWriter.add(
          file.path,
          new zip.BlobReader(fileData),
          fileAddOptions
        );
        filesProcessed++;
      }

      setCompressionProgress((prev) => ({
        ...prev,
        title: "ZIPファイルを構築中...",
        message: "最終処理を行っています...",
        progress: 100,
      }));
      const zipBlob = await zipWriter.close();

      if (password) {
        setLastCompressedInfo({ fileName: finalArchiveName, password });
      } else {
        setLastCompressedInfo(null);
      }

      const triggerDownload = (blob, name) => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
      };

      if (isSplitEnabled) {
        setCompressionProgress({
          title: "ファイルを分割中...",
          message: "ダウンロードリストを作成しています...",
          progress: 100,
        });
        const chunkSize = splitSize * 1024 * 1024;
        const parts = [];
        let offset = 0;
        let partIndex = 1;
        while (offset < zipBlob.size) {
          const end = offset + chunkSize;
          const chunk = zipBlob.slice(offset, end);
          const baseName = finalArchiveName.replace(/\.zip$/i, "");
          const ext = `.${String(partIndex).padStart(3, "0")}`;
          const filename = `${baseName}.zip${ext}`;
          parts.push({ name: filename, blob: chunk });
          offset = end;
          partIndex++;
        }
        setDownloadList(parts);
      } else {
        triggerDownload(zipBlob, finalArchiveName);
      }
    } catch (error) {
      console.error("圧縮エラー:", error);
      alert(`ファイルの圧縮に失敗しました: ${error.message}`);
    } finally {
      setCompressionProgress(null);
    }
  };

  const handleCompress = async () => {
    const filesToCompress = files.filter((f) => !f.isDir && f.rawFile);
    if (filesToCompress.length === 0) {
      alert("圧縮するファイルがありません。");
      return;
    }

    const finalArchiveName =
      archiveName && archiveName.toLowerCase().endsWith(".zip")
        ? archiveName
        : "archive.zip";

    if (isSplitEnabled) {
      setSplitPromptInput(finalArchiveName);
      setSplitFileNamePrompt({
        onConfirm: (newName) => {
          executeCompression(newName);
        },
      });
    } else {
      executeCompression(finalArchiveName);
    }
  };
  //===4_4.HANDLERS_ZipOperations=End==

  //===4_5.HANDLERS_Item_Interaction=Start==
  const handleDownload = async (file) => {
    if (file.isDir) return;
    if (file.rawFile instanceof window.File || file.rawFile instanceof Blob) {
      const link = document.createElement("a");
      link.href = URL.createObjectURL(file.rawFile);
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      try {
        const data = await file.rawFile.getData(
          new globalThis.zip.BlobWriter(),
          { password: decryptionPassword, useWebWorkers: true }
        );
        const link = document.createElement("a");
        link.href = URL.createObjectURL(data);
        link.download = file.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error("ファイルデータの取得エラー:", error);
        alert(
          "ファイルデータの取得に失敗しました。パスワードが間違っている可能性があります。"
        );
      }
    }
  };

  const handleItemActivate = async (file) => {
    if (navigationLock.current) return;

    if (file.isDir) {
      navigationLock.current = true;
      handleClearSelection();
      setCurrentPath(file.path);
      setTimeout(() => {
        navigationLock.current = false;
      }, 200);
      return;
    }
    try {
      if (!file.rawFile) {
        alert("プレビュー対象のファイルデータが見つかりません。");
        return;
      }
      let fileDataBlob;
      if (file.rawFile instanceof window.File || file.rawFile instanceof Blob) {
        fileDataBlob = file.rawFile;
      } else {
        fileDataBlob = await file.rawFile.getData(
          new globalThis.zip.BlobWriter(),
          { password: decryptionPassword, useWebWorkers: true }
        );
      }

      // ▼▼▼ REFACTOR: Centralized Size Limit Logic ▼▼▼
      const sizeLimits = {
        high: 30 * 1024 * 1024, // 30MB
        default: 5 * 1024 * 1024, // 5MB
      };
      const highLimitExtensions = ["psd", "pdf", "ai"];
      const lowerCaseName = file.name.toLowerCase();
      const extension = lowerCaseName.split(".").pop();
      const sizeLimit = highLimitExtensions.includes(extension)
        ? sizeLimits.high
        : sizeLimits.default;

      if (fileDataBlob.size > sizeLimit) {
        handleDownload(file);
        return;
      }
      // ▲▲▲ REFACTOR: Centralized Size Limit Logic ▲▲▲

      const mimeType = getMimeTypeFromFilename(file.name);
      const typedBlob =
        fileDataBlob.type === mimeType
          ? fileDataBlob
          : new Blob([fileDataBlob], { type: mimeType });

      if (
        lowerCaseName.endsWith(".pdf") ||
        (lowerCaseName.endsWith(".ai") && file.isPdfCompatible)
      ) {
        setPdfPreviewData({ file, content: typedBlob });
      } else if (
        lowerCaseName.endsWith(".csv") ||
        lowerCaseName.endsWith(".tsv")
      ) {
        const buffer = await typedBlob.arrayBuffer();

        const { content, detectedEncoding } = await new Promise(
          (resolve, reject) => {
            if (!globalThis.jschardet) {
              return reject(
                new Error(
                  "文字コード判別ライブラリの読み込みに失敗しました。ページを再読み込みしてください。"
                )
              );
            }

            const reader = new FileReader();
            reader.onload = (e) => {
              try {
                const binaryString = e.target.result;
                const detected = globalThis.jschardet.detect(binaryString);
                const encoding =
                  detected && detected.confidence > 0.8
                    ? detected.encoding
                    : "utf-8";

                let decodedContent;
                try {
                  const decoder = new TextDecoder(encoding.toLowerCase());
                  decodedContent = decoder.decode(buffer);
                } catch (err) {
                  const decoder = new TextDecoder("utf-8");
                  decodedContent = decoder.decode(buffer);
                }
                resolve({
                  content: decodedContent,
                  detectedEncoding: encoding,
                });
              } catch (err) {
                reject(err);
              }
            };
            reader.onerror = (e) =>
              reject(new Error("ファイルの読み込みに失敗しました。"));
            reader.readAsBinaryString(typedBlob);
          }
        );

        setCsvPreviewData({ file, content, buffer, detectedEncoding });
      } else if (lowerCaseName.endsWith(".docx")) {
        const buffer = await typedBlob.arrayBuffer();
        setDocxPreviewData({ file, content: buffer });
      } else if (
        lowerCaseName.endsWith(".xlsx") ||
        lowerCaseName.endsWith(".xls")
      ) {
        const { XLSX } = globalThis;
        if (!XLSX) {
          alert("Excelプレビューライブラリの読み込みに失敗しました。");
          return;
        }
        const buffer = await typedBlob.arrayBuffer();
        const workbook = XLSX.read(buffer, { type: "array", bookViews: true });

        setXlsxPreviewData({ file, workbook });
      } else if (lowerCaseName.endsWith(".psd")) {
        const { agPsd } = globalThis;
        if (!agPsd) {
          alert("PSDプレビューライブラリ(ag-psd)の読み込みに失敗しました。");
          return;
        }
        const buffer = await typedBlob.arrayBuffer();
        const psd = agPsd.readPsd(buffer);
        if (psd.canvas) {
          const dataUrl = psd.canvas.toDataURL();
          setPreviewData({ file, content: dataUrl });
        } else {
          throw new Error(
            "このPSDファイルにはプレビュー可能な画像が含まれていません。"
          );
        }
      } else if (typedBlob.type.startsWith("image/")) {
        const content = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target.result);
          reader.onerror = (e) => reject(e);
          reader.readAsDataURL(typedBlob);
        });
        setPreviewData({ file, content });
      } else if (
        typedBlob.type.startsWith("text/") ||
        typedBlob.type === "application/json" ||
        typedBlob.type === "application/javascript"
      ) {
        const content = await typedBlob.text();
        setPreviewData({ file, content });
      } else {
        handleDownload(file);
      }
    } catch (error) {
      console.error("プレビューエラー:", error);
      alert(
        "ファイルのプレビューに失敗しました。パスワードが間違っている可能性があります。"
      );
    }
  };

  const handleSort = (key) => {
    setSortConfig((prevConfig) => ({
      key,
      order:
        prevConfig.key === key && prevConfig.order === "asc" ? "desc" : "asc",
    }));
  };

  const handleFileSelect = (clickedId, e, sortedFiles) => {
    if (navigationLock.current) return;

    e.stopPropagation();
    setSelectedFileIds((prevSelectedIds) => {
      const newSelectedIds = new Set(prevSelectedIds);
      if (e.shiftKey && lastClickedId) {
        const lastIndex = sortedFiles.findIndex((f) => f.id === lastClickedId);
        const currentIndex = sortedFiles.findIndex((f) => f.id === clickedId);
        const [start, end] = [lastIndex, currentIndex].sort((a, b) => a - b);
        newSelectedIds.clear();
        for (let i = start; i <= end; i++) {
          newSelectedIds.add(sortedFiles[i].id);
        }
      } else if (e.ctrlKey || e.metaKey) {
        if (newSelectedIds.has(clickedId)) {
          newSelectedIds.delete(clickedId);
        } else {
          newSelectedIds.add(clickedId);
        }
        setLastClickedId(clickedId);
      } else {
        if (newSelectedIds.has(clickedId) && newSelectedIds.size === 1) {
          newSelectedIds.clear();
        } else {
          newSelectedIds.clear();
          newSelectedIds.add(clickedId);
        }
        setLastClickedId(clickedId);
      }
      return newSelectedIds;
    });
  };

  const handleClearSelection = () => {
    setSelectedFileIds(new Set());
    setLastClickedId(null);
  };
  //===4_5.HANDLERS_Item_Interaction=End==

  //===4_6.HANDLERS_Clipboard_Delete=Start==
  const handleDeleteFiles = () => {
    if (selectedFileIds.size === 0) {
      return;
    }

    const idsToDelete = new Set(selectedFileIds);

    files.forEach((f) => {
      if (selectedFileIds.has(f.id) && f.isDir) {
        files.forEach((child) => {
          if (child.path.startsWith(f.path) && child.id !== f.id) {
            idsToDelete.add(child.id);
          }
        });
      }
    });

    const remainingFiles = files.filter((f) => !idsToDelete.has(f.id));
    recordHistory(remainingFiles);
    handleClearSelection();
  };

  const handleCopy = () => {
    if (selectedFileIds.size === 0) return;
    setClipboard({ type: "copy", ids: new Set(selectedFileIds) });
  };

  const handleCut = () => {
    if (selectedFileIds.size === 0) return;
    setClipboard({ type: "cut", ids: new Set(selectedFileIds) });
  };

  const handlePaste = (targetPath) => {
    if (!clipboard.type || clipboard.ids.size === 0) return;

    let currentFiles = [...files];
    let idCounter = Date.now();

    const getParentPath = (path, isDir) => {
      if (!path) return "";
      const effectivePath = isDir ? path.slice(0, -1) : path;
      const lastSlash = effectivePath.lastIndexOf("/");
      return lastSlash === -1 ? "" : effectivePath.substring(0, lastSlash + 1);
    };

    const getUniqueName = (
      name,
      targetDir,
      fileIdToExclude,
      newlyCreatedNames
    ) => {
      const siblingNames = new Set(
        currentFiles
          .filter((f) => {
            if (f.id === fileIdToExclude) return false;
            const parent = getParentPath(f.path, f.isDir);
            return parent === targetDir;
          })
          .map((f) => f.name)
      );
      newlyCreatedNames.forEach((n) => siblingNames.add(n));

      if (!siblingNames.has(name)) {
        return name;
      }

      const nameParts = name.split(".");
      const ext = nameParts.length > 1 ? `.${nameParts.pop()}` : "";
      const baseName = nameParts.join(".").replace(/ \(\d+\)$/, "");

      let counter = 1;
      let newName;
      do {
        newName = `${baseName} (${counter})${ext}`;
        counter++;
      } while (siblingNames.has(newName));

      return newName;
    };

    if (clipboard.type === "copy") {
      const newItems = [];
      const newlyCreatedNames = new Set();
      const itemsToProcess = currentFiles
        .filter((f) => clipboard.ids.has(f.id))
        .sort((a, b) => a.name.localeCompare(b.name, "ja", { numeric: true }));

      for (const item of itemsToProcess) {
        if (
          itemsToProcess.some(
            (p) => p.isDir && item.path.startsWith(p.path) && p.id !== item.id
          )
        ) {
          continue;
        }

        const newName = getUniqueName(
          item.name,
          targetPath,
          null,
          newlyCreatedNames
        );
        newlyCreatedNames.add(newName);
        const newPath = `${targetPath}${newName}${item.isDir ? "/" : ""}`;

        const newItem = {
          ...item,
          id: idCounter++,
          name: newName,
          path: newPath,
        };
        newItems.push(newItem);

        if (item.isDir) {
          const children = currentFiles.filter(
            (f) => f.path.startsWith(item.path) && f.id !== item.id
          );
          for (const child of children) {
            const childNewPath = child.path.replace(item.path, newPath);
            newItems.push({ ...child, id: idCounter++, path: childNewPath });
          }
        }
      }
      recordHistory([...currentFiles, ...newItems]);
    } else if (clipboard.type === "cut") {
      const itemsToMoveIds = new Set(clipboard.ids);

      const updatedFiles = currentFiles.map((f) => {
        if (itemsToMoveIds.has(f.id)) {
          const parentPath = getParentPath(f.path, f.isDir);
          if (parentPath === targetPath) return f;

          const newName = getUniqueName(f.name, targetPath, f.id, new Set());
          const newPath = `${targetPath}${newName}${f.isDir ? "/" : ""}`;

          if (f.isDir) {
            currentFiles.forEach((child) => {
              if (child.path.startsWith(f.path) && child.id !== f.id) {
                child.path = child.path.replace(f.path, newPath);
              }
            });
          }
          return { ...f, name: newName, path: newPath };
        }
        return f;
      });

      recordHistory(updatedFiles);
      setClipboard({ type: null, ids: new Set() });
    }

    handleClearSelection();
  };
  //===4_6.HANDLERS_Clipboard_Delete=End==

  //===4_7.HANDLERS_ContextMenu=Start==
  const handleItemContextMenu = (e, fileId) => {
    e.preventDefault();
    e.stopPropagation();
    if (!selectedFileIds.has(fileId)) {
      setSelectedFileIds(new Set([fileId]));
      setLastClickedId(fileId);
    }
    setContextMenu({ x: e.clientX, y: e.clientY, path: currentPath });
  };

  const handleTreeFolderContextMenu = (e, path) => {
    e.preventDefault();
    e.stopPropagation();

    const folderObject = files.find((f) => f.path === path);
    if (folderObject && !selectedFileIds.has(folderObject.id)) {
      setSelectedFileIds(new Set([folderObject.id]));
      setLastClickedId(folderObject.id);
    } else if (!folderObject && path === "") {
      handleClearSelection();
    }

    setContextMenu({ x: e.clientX, y: e.clientY, type: "tree", path: path });
  };

  const handleBackgroundContextMenu = (e) => {
    e.preventDefault();
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      isBackground: true,
      path: currentPath,
    });
  };

  const handleCloseContextMenu = () => setContextMenu(null);
  //===4_7.HANDLERS_ContextMenu=End==

  //===4_8.HANDLERS_History=Start==
  const recordHistory = (newFiles) => {
    const newSnapshots = history.snapshots.slice(0, history.current + 1);
    newSnapshots.push(newFiles);

    setFiles(newFiles);
    updateFolderTree(newFiles);
    setIsModified(true);
    setLastCompressedInfo(null);

    setHistory({
      snapshots: newSnapshots,
      current: newSnapshots.length - 1,
    });
  };

  const handleUndo = () => {
    if (history.current > 0) {
      const newIndex = history.current - 1;
      const prevFiles = history.snapshots[newIndex];
      setHistory({ ...history, current: newIndex });
      setFiles(prevFiles);
      updateFolderTree(prevFiles);
      setLastCompressedInfo(null);
    }
  };

  const handleRedo = () => {
    if (history.current < history.snapshots.length - 1) {
      const newIndex = history.current + 1;
      const nextFiles = history.snapshots[newIndex];
      setHistory({ ...history, current: newIndex });
      setFiles(nextFiles);
      updateFolderTree(nextFiles);
      setLastCompressedInfo(null);
    }
  };
  //===4_8.HANDLERS_History=End==

  //===5_1.RENDER_Data_Preparation=Start==
  const displayedFiles = React.useMemo(() => {
    return files.filter((file) => {
      const currentPathSegments = currentPath.split("/").filter(Boolean);
      const filePathSegments = file.path.replace(/\/$/, "").split("/");
      if (currentPath === "") {
        return filePathSegments.length === 1;
      } else {
        return (
          file.path.startsWith(currentPath) &&
          filePathSegments.length === currentPathSegments.length + 1
        );
      }
    });
  }, [files, currentPath]);

  const sortedFiles = React.useMemo(() => {
    return [...displayedFiles].sort((a, b) => {
      if (a.isDir && !b.isDir) return -1;
      if (!a.isDir && b.isDir) return 1;
      const { key, order } = sortConfig;
      let comparison = 0;
      const getFileTypeString = (file) => {
        if (file.isDir) return " フォルダ";
        const parts = file.name.split(".");
        if (parts.length > 1) return parts.pop().toUpperCase();
        return "ファイル";
      };
      switch (key) {
        case "name":
          comparison = a.name.localeCompare(b.name, "ja", { numeric: true });
          break;
        case "type":
          comparison = getFileTypeString(a).localeCompare(
            getFileTypeString(b),
            "ja"
          );
          break;
        case "size":
          if (a.isDir) {
            comparison = a.name.localeCompare(b.name, "ja", { numeric: true });
          } else {
            comparison = a.size - b.size;
          }
          break;
        case "modified":
          comparison =
            (a.modified?.getTime() || 0) - (b.modified?.getTime() || 0);
          break;
        default:
          break;
      }
      return order === "asc" ? comparison : -comparison;
    });
  }, [displayedFiles, sortConfig]);

  const isFilePreviewable = (file) => {
    if (!file || file.isDir) return false;

    const sizeLimits = {
      high: 30 * 1024 * 1024, // 30MB
      default: 5 * 1024 * 1024, // 5MB
    };
    const highLimitExtensions = ["psd", "pdf", "ai"];
    const lowerCaseName = file.name.toLowerCase();
    const extension = lowerCaseName.split(".").pop();
    const sizeLimit = highLimitExtensions.includes(extension)
      ? sizeLimits.high
      : sizeLimits.default;

    if (file.size > sizeLimit) return false;

    const imageExtensions = ["jpg", "jpeg", "png", "gif", "webp", "svg"];
    const textExtensions = ["txt", "md", "json", "js", "css", "html", "xml"];
    const tableExtensions = ["csv", "tsv"];
    const officeExtensions = ["docx", "xlsx", "xls"];
    const psdExtension = "psd";

    if (imageExtensions.some((ext) => lowerCaseName.endsWith(`.${ext}`)))
      return true;
    if (textExtensions.some((ext) => lowerCaseName.endsWith(`.${ext}`)))
      return true;
    if (tableExtensions.some((ext) => lowerCaseName.endsWith(`.${ext}`)))
      return true;
    if (officeExtensions.some((ext) => lowerCaseName.endsWith(`.${ext}`)))
      return true;
    if (lowerCaseName.endsWith(`.${psdExtension}`)) return true;
    if (lowerCaseName.endsWith(".pdf")) return true;
    if (lowerCaseName.endsWith(".ai") && file.isPdfCompatible) return true;

    return false;
  };

  const getContextMenuItems = () => {
    if (!contextMenu) return [];

    const { isBackground, type, path } = contextMenu;
    const targetPath = path ?? currentPath;
    const canPaste = clipboard.type !== null && clipboard.ids.size > 0;

    const singleSelectedFile =
      selectedFileIds.size === 1
        ? files.find((f) => selectedFileIds.has(f.id))
        : null;

    const previewItem = {
      label: "プレビュー",
      icon: <Eye size={16} />,
      action: () => {
        if (singleSelectedFile) handleItemActivate(singleSelectedFile);
      },
      disabled: !isFilePreviewable(singleSelectedFile),
    };

    const undoItem = {
      label: "元に戻す (Ctrl+Z)",
      icon: <RotateCcw size={16} />,
      action: handleUndo,
      disabled: history.current <= 0,
    };
    const redoItem = {
      label: "やり直し (Ctrl+Y)",
      icon: <RotateCw size={16} />,
      action: handleRedo,
      disabled: history.current >= history.snapshots.length - 1,
    };

    const pasteItem = {
      label: "貼り付け (Ctrl+V)",
      icon: <FilePlus size={16} />,
      action: () => handlePaste(targetPath),
      disabled: !canPaste,
    };

    const backgroundItems = [
      {
        label: "ファイルを追加",
        icon: <FilePlus size={16} />,
        action: () => handleFileAddClick(targetPath),
      },
      {
        label: "新しいフォルダ",
        icon: <FolderPlus size={16} />,
        action: handleNewFolder,
      },
      { separator: true },
      undoItem,
      redoItem,
      { separator: true },
      pasteItem,
    ];

    if (isBackground) {
      return backgroundItems;
    }

    const isRootSelected =
      selectedFileIds.size === 1 &&
      files.find((f) => selectedFileIds.has(f.id) && f.path === "") !==
        undefined;

    const actionItems = [
      previewItem,
      { separator: true },
      undoItem,
      redoItem,
      { separator: true },
      {
        label: "コピー (Ctrl+C)",
        icon: <Download size={16} />,
        action: handleCopy,
        disabled: selectedFileIds.size === 0,
      },
      {
        label: "カット (Ctrl+X)",
        icon: <Download size={16} />,
        action: handleCut,
        disabled:
          selectedFileIds.size === 0 ||
          (type === "tree" && path === "") ||
          isRootSelected,
      },
      pasteItem,
      { separator: true },
      {
        label: "名前の変更 (F2)",
        icon: <Edit size={16} />,
        action: handleRename,
        disabled: selectedFileIds.size !== 1,
      },
      {
        label: "削除 (Del)",
        icon: <Trash2 size={16} />,
        action: handleDeleteFiles,
        disabled: selectedFileIds.size === 0,
      },
    ];

    if (type === "tree") {
      if (path === "") {
        return backgroundItems;
      }
      return actionItems;
    } else {
      return actionItems;
    }
  };
  //===5_1.RENDER_Data_Preparation=End==

  //===5_2.RENDER_Background_Interaction_Handlers=Start==
  const rootLongPressTimerId = useRef(null);
  const rootIsLongPressHandled = useRef(false);
  const handleRootPressStart = (e) => {
    if (
      e.target.closest('[data-is-item="true"]') &&
      e.currentTarget !== e.target.closest('[data-is-item="true"]')
    )
      return;
    rootIsLongPressHandled.current = false;
    const eventForLongPress = {
      clientX: e.clientX ?? e.touches.clientX,
      clientY: e.clientY ?? e.touches.clientY,
      preventDefault: () => e.preventDefault(),
      stopPropagation: () => e.stopPropagation(),
    };
    rootLongPressTimerId.current = setTimeout(() => {
      handleTreeFolderContextMenu(eventForLongPress, "");
      rootIsLongPressHandled.current = true;
    }, 400);
  };
  const handleRootPressEnd = (e) => {
    if (
      e.target.closest('[data-is-item="true"]') &&
      e.currentTarget !== e.target.closest('[data-is-item="true"]')
    )
      return;
    clearTimeout(rootLongPressTimerId.current);
    if (rootIsLongPressHandled.current) {
      e.preventDefault();
      return;
    }
    if (e.button === 2) {
      return;
    }
    setCurrentPath("");
    setIsSidebarOpen(false);
  };
  const handleRootContextMenu = (e) => {
    e.preventDefault();
    clearTimeout(rootLongPressTimerId.current);
    handleTreeFolderContextMenu(e, "");
  };

  const backgroundLongPressTimerId = useRef(null);
  const backgroundIsLongPressHandled = useRef(false);

  const handleBackgroundTouchStart = (e) => {
    if (e.target.closest('[data-is-item="true"]')) return;
    backgroundIsLongPressHandled.current = false;
    const eventForLongPress = {
      clientX: e.touches.clientX,
      clientY: e.touches.clientY,
      preventDefault: () => e.preventDefault(),
      stopPropagation: () => e.stopPropagation(),
    };
    backgroundLongPressTimerId.current = setTimeout(() => {
      handleBackgroundContextMenu(eventForLongPress);
      backgroundIsLongPressHandled.current = true;
    }, 400);
  };

  const handleBackgroundTouchEnd = (e) => {
    if (e.target.closest('[data-is-item="true"]')) return;
    clearTimeout(backgroundLongPressTimerId.current);
    if (backgroundIsLongPressHandled.current) {
      e.preventDefault();
      return;
    }
    handleClearSelection();
  };

  const handleBackgroundMouseDown = (e) => {
    if (e.button !== 0) return;
    if (e.target.closest('[data-is-item="true"]')) return;
    handleClearSelection();
  };

  const handleBackgroundContextMenuCleared = (e) => {
    e.preventDefault();
    clearTimeout(backgroundLongPressTimerId.current);
    handleBackgroundContextMenu(e);
  };
  //===5_2.RENDER_Background_Interaction_Handlers=End==

  //===5_3.RENDER_JSX_Modals_And_Overlays=Start==
  // ▼▼▼ FEATURE-SESSION: Restore Modal Component ▼▼▼
  const RestoreSessionModal = ({ sessionData, onRestore, onDiscard }) => {
    const [restorePassword, setRestorePassword] = useState("");
    const [error, setError] = useState("");
    const { passwordHash } = sessionData;

    const handleRestore = async () => {
      if (!passwordHash) {
        onRestore(sessionData);
        return;
      }
      const inputHash = await hashPassword(restorePassword);
      if (inputHash === passwordHash) {
        onRestore(sessionData, restorePassword);
      } else {
        setError("パスワードが違います。");
      }
    };

    return (
      <ConfirmationModal
        title={passwordHash ? "保護されたセッションの復元" : "セッションの復元"}
        message={
          passwordHash
            ? "この作業データはパスワードで保護されています。復元するにはZIPに設定したパスワードを入力してください。"
            : "前回終了されなかった作業データが見つかりました。復元しますか？"
        }
      >
        <div className="w-full">
          {passwordHash && (
            <div className="mb-4">
              <input
                type="password"
                value={restorePassword}
                onChange={(e) => {
                  setRestorePassword(e.target.value);
                  setError("");
                }}
                className="w-full py-2 px-3 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-400"
                placeholder="パスワード"
                autoFocus
              />
              {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
            </div>
          )}
          <div className="flex justify-end gap-4">
            <button
              onClick={onDiscard}
              className="px-4 py-2 rounded-lg bg-slate-200 text-slate-700 hover:bg-slate-300 transition-colors"
            >
              いいえ（新規作成）
            </button>
            <button
              onClick={handleRestore}
              disabled={passwordHash && !restorePassword}
              className="px-4 py-2 rounded-lg bg-violet-500 text-white hover:bg-violet-600 transition-colors disabled:bg-slate-300"
            >
              復元する
            </button>
          </div>
        </div>
      </ConfirmationModal>
    );
  };
  // ▲▲▲ FEATURE-SESSION: Restore Modal Component ▲▲▲

  return (
    <div
      ref={appContainerRef}
      className="flex flex-col bg-sky-50 text-slate-700"
      // ▼▼▼ UI-FIX: Disable background drag/drop when modal is open ▼▼▼
      onDrop={splitRestoreRequest ? (e) => e.preventDefault() : handleDrop}
      onDragOver={
        splitRestoreRequest ? (e) => e.preventDefault() : handleDragOver
      }
      onDragEnter={
        splitRestoreRequest ? (e) => e.preventDefault() : handleDragEnter
      }
      onDragLeave={
        splitRestoreRequest ? (e) => e.preventDefault() : handleDragLeave
      }
      // ▲▲▲ UI-FIX: Disable background drag/drop when modal is open ▲▲▲
    >
      <input
        type="file"
        multiple
        ref={fileInputRef}
        onChange={handleFileAddChange}
        className="hidden"
      />
      {compressionProgress && (
        <ProgressModal
          title={compressionProgress.title}
          message={compressionProgress.message}
          progress={compressionProgress.progress}
        />
      )}
      {isResetModalVisible && (
        <ConfirmationModal
          title="確認"
          message="現在の作業内容をクリアします。保存されたセッションデータも削除されます。よろしいですか？"
          confirmText="はい、クリアします"
          onConfirm={confirmReset}
          onCancel={() => setIsResetModalVisible(false)}
        />
      )}
      {splitFileNamePrompt && (
        <ConfirmationModal
          title="ファイル名の確認"
          message="分割圧縮するファイルの名前を指定してください。"
        >
          <div className="w-full">
            <input
              type="text"
              value={splitPromptInput}
              onChange={(e) => setSplitPromptInput(e.target.value)}
              className="w-full py-2 px-3 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-400 mb-6"
              onFocus={(e) => e.target.select()}
              autoFocus
            />
            <div className="flex justify-end gap-4">
              <button
                onClick={() => setSplitFileNamePrompt(null)}
                className="px-4 py-2 rounded-lg bg-slate-200 text-slate-700 hover:bg-slate-300 transition-colors"
              >
                いいえ
              </button>
              <button
                onClick={() => {
                  splitFileNamePrompt.onConfirm(splitPromptInput);
                  setSplitFileNamePrompt(null);
                }}
                disabled={!splitPromptInput}
                className="px-4 py-2 rounded-lg bg-violet-500 text-white hover:bg-violet-600 transition-colors disabled:bg-slate-300"
              >
                はい
              </button>
            </div>
          </div>
        </ConfirmationModal>
      )}
      {downloadList && (
        <DownloadListModal
          downloads={downloadList}
          onDownload={(blob, name) => {
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = name;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(link.href);
          }}
          onClose={() => setDownloadList(null)}
        />
      )}
      {splitRestoreRequest && (
        <SplitRestoreModal
          initialFile={splitRestoreRequest}
          onUnzip={handleCombineAndDecompress}
          onCancel={() => setSplitRestoreRequest(null)}
        />
      )}
      {sessionRestoreRequest && (
        <RestoreSessionModal
          sessionData={sessionRestoreRequest}
          onRestore={(data, restoredPassword) => {
            setFiles(data.files);
            updateFolderTree(data.files);
            setArchiveName(data.archiveName);
            if (restoredPassword) {
              setDecryptionPassword(restoredPassword);
              setPassword(restoredPassword);
            }
            setHistory({ snapshots: [data.files], current: 0 });
            setSessionRestoreRequest(null);
          }}
          onDiscard={async () => {
            await deleteSession();
            setSessionRestoreRequest(null);
          }}
        />
      )}
      {replaceConfirm && (
        <ConfirmationModal
          title="ファイルを開く"
          message={
            <>
              <p>現在のリストへの変更は保存されません。</p>
              <p className="mt-2">
                '{replaceConfirm.file.name}' を開きますか？
              </p>
            </>
          }
        >
          <button
            onClick={() => setReplaceConfirm(null)}
            className="px-4 py-2 rounded-lg bg-slate-200 text-slate-700 hover:bg-slate-300 transition-colors"
          >
            いいえ
          </button>
          <button
            onClick={() => {
              const { file, path } = replaceConfirm;
              const entry = {
                filename: file.name,
                directory: false,
                uncompressedSize: file.size,
                lastModDate: new Date(file.lastModified),
                getRawFile: () => file,
              };
              const entriesToAdd = [entry];
              if (path) {
                entriesToAdd.forEach(
                  (e) => (e.filename = `${path}${e.filename}`)
                );
              }
              processUnzippedData(entriesToAdd, archiveName, true);
              setReplaceConfirm(null);
            }}
            className="px-4 py-2 rounded-lg bg-sky-500 text-white hover:bg-sky-600 transition-colors"
          >
            ファイルを追加
          </button>
          <button
            onClick={() => {
              confirmReset();
              handleDecompress(replaceConfirm.file, password);
              setReplaceConfirm(null);
            }}
            className="px-4 py-2 rounded-lg bg-violet-500 text-white hover:bg-violet-600 transition-colors"
          >
            はい
          </button>
        </ConfirmationModal>
      )}
      {previewData && (
        <PreviewModal
          file={previewData.file}
          content={previewData.content}
          onClose={() => setPreviewData(null)}
          onDownload={handleDownload}
        />
      )}
      {csvPreviewData && (
        <CsvPreviewModal
          file={csvPreviewData.file}
          content={csvPreviewData.content}
          buffer={csvPreviewData.buffer}
          detectedEncoding={csvPreviewData.detectedEncoding}
          onClose={() => setCsvPreviewData(null)}
          onDownload={handleDownload}
        />
      )}
      {pdfPreviewData && (
        <PdfPreviewModal
          file={pdfPreviewData.file}
          content={pdfPreviewData.content}
          onClose={() => setPdfPreviewData(null)}
          onDownload={handleDownload}
        />
      )}
      {docxPreviewData && (
        <DocxPreviewModal
          file={docxPreviewData.file}
          content={docxPreviewData.content}
          onClose={() => setDocxPreviewData(null)}
          onDownload={handleDownload}
        />
      )}
      {xlsxPreviewData && (
        <XlsxPreviewModal
          file={xlsxPreviewData.file}
          workbook={xlsxPreviewData.workbook}
          onClose={() => setXlsxPreviewData(null)}
          onDownload={handleDownload}
        />
      )}
      {unzipRequest && (
        <PasswordModal
          fileName={unzipRequest.name}
          onConfirm={handleUnzipWithPassword}
          onCancel={() => setUnzipRequest(null)}
        />
      )}
      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          items={getContextMenuItems()}
          onClose={handleCloseContextMenu}
        />
      )}
      {prompt?.type === "newFolder" && (
        <PromptModal
          title="新しいフォルダ"
          placeholder="フォルダ名"
          onConfirm={prompt.onConfirm}
          onCancel={() => setPrompt(null)}
          confirmText="作成"
        />
      )}
      {prompt?.type === "rename" && (
        <PromptModal
          title="名前の変更"
          initialValue={prompt.initialValue}
          onConfirm={prompt.onConfirm}
          onCancel={() => setPrompt(null)}
          confirmText="変更"
        />
      )}
      {isShareModalVisible && lastCompressedInfo && (
        <ShareModal
          info={lastCompressedInfo}
          onClose={() => setIsShareModalVisible(false)}
        />
      )}
      {/*===5_3.RENDER_JSX_Modals_And_Overlays=End==*/}

      {/*===5_4.RENDER_JSX_Header=Start==*/}
      <header className="flex items-center justify-between p-3 bg-white border-b border-sky-200 shadow-sm shrink-0 z-30">
        {!isSelectionMode ? (
          <>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setIsSidebarOpen(true)}
                className="md:hidden p-2 rounded-md hover:bg-slate-100"
                aria-label="Open sidebar"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="4" x2="20" y1="6" y2="6" />
                  <line x1="4" x2="20" y1="12" y2="12" />
                  <line x1="4" x2="20" y1="18" y2="18" />
                </svg>
              </button>
              <FileArchive className="text-violet-500" size={32} />
              <h1 className="text-xl md:text-2xl font-bold text-slate-800 tracking-wider whitespace-nowrap">
                きゅっとFile
              </h1>
            </div>
            <div className="flex-grow flex items-center justify-center mx-4">
              <div className="hidden md:block">
                <a
                  href="//af.moshimo.com/af/c/click?a_id=5147250&p_id=2540&pc_id=5631&pl_id=33157"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src="//image.moshimo.com/af-img/1762/000000033157.png"
                    width="468"
                    height="60"
                    style={{ border: "none" }}
                    alt="ad"
                  />
                </a>
                <img
                  src="//i.moshimo.com/af/i/impression?a_id=5147250&p_id=2540&pc_id=5631&pl_id=33157"
                  width="1"
                  height="1"
                  style={{ border: "none" }}
                  loading="lazy"
                  alt=""
                />
              </div>
              <div className="md:hidden w-full flex justify-end">
                {/* ▼▼▼ BUG FIX ▼▼▼ */}
                <a
                  href="//af.moshimo.com/af/c/click?a_id=5148799&p_id=4936&pc_id=13170&pl_id=68077"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {/* ▲▲▲ BUG FIX ▲▲▲ */}
                  <img
                    src="//image.moshimo.com/af-img/1762/000000068077.gif"
                    width="100"
                    height="60"
                    style={{ border: "none" }}
                    alt="ad-mobile"
                  />
                </a>
                <img
                  src="//i.moshimo.com/af/i/impression?a_id=5148799&p_id=4936&pc_id=13170&pl_id=68077"
                  width="1"
                  height="1"
                  style={{ border: "none" }}
                  loading="lazy"
                  alt=""
                />
              </div>
            </div>
          </>
        ) : (
          <div className="flex items-center justify-between w-full">
            <button
              onClick={() => {
                setIsSelectionMode(false);
                handleClearSelection();
              }}
              className="px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-lg"
            >
              キャンセル
            </button>
            <span className="font-bold">{selectedFileIds.size}個選択中</span>
            <div />
          </div>
        )}
      </header>
      {/*===5_4.RENDER_JSX_Header=End==*/}

      {/*===5_5.RENDER_JSX_Main_Content=Start==*/}
      <div className="flex flex-1 overflow-hidden relative">
        <aside
          className={`absolute md:static w-64 bg-white p-4 border-r border-sky-200 z-40 h-full
transition-transform duration-300 ease-in-out
${isSidebarOpen ? "translate-x-0" : "-translate-x-full"}
md:translate-x-0
${isDraggingOver ? "overflow-hidden" : "overflow-y-auto"}`}
        >
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Folder className="text-violet-500" />
              <span>{archiveName || "ファイル一覧"}</span>
            </h2>
            <button
              onClick={() => setIsSidebarOpen(false)}
              className="md:hidden p-2 rounded-md hover:bg-slate-100"
              aria-label="Close sidebar"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          <ul className="space-y-1 text-sm">
            <li key="root-folder">
              <div
                onMouseDown={handleRootPressStart}
                onMouseUp={handleRootPressEnd}
                onTouchStart={handleRootPressStart}
                onTouchEnd={handleRootPressEnd}
                onContextMenu={handleRootContextMenu}
                data-is-item="true"
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onDragEnter={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setTreeDropTarget("");
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!e.currentTarget.contains(e.relatedTarget))
                    setTreeDropTarget(null);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (treeDropTarget === "") handleDropIntoTreeFolder(e, "");
                  setTreeDropTarget(null);
                }}
                className={`long-press-target flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
                  currentPath === ""
                    ? "bg-violet-100 text-violet-700 font-bold"
                    : "hover:bg-sky-100"
                } ${
                  treeDropTarget === "" ? "bg-green-200" : ""
                } bg-green-500/20`}
              >
                <Folder size={18} className="pointer-events-none" />
                <span className="pointer-events-none">(ルート)</span>
              </div>
            </li>
            {(archiveName || files.length > 0) && (
              <FolderTreeView
                tree={folderTree}
                onSelect={(path) => {
                  setCurrentPath(path);
                  setIsSidebarOpen(false);
                }}
                selectedPath={currentPath}
                treeDropTarget={treeDropTarget}
                setTreeDropTarget={setTreeDropTarget}
                onDropIntoTreeFolder={handleDropIntoTreeFolder}
                onContextMenu={handleTreeFolderContextMenu}
                files={files}
                clipboard={clipboard}
              />
            )}
          </ul>
        </aside>
        {isSidebarOpen && (
          <div
            onClick={() => setIsSidebarOpen(false)}
            className="md:hidden absolute inset-0 bg-black/30 z-30"
          ></div>
        )}

        <main className="flex-1 flex flex-col p-4">
          <div className="flex flex-wrap items-center justify-start gap-2 mb-4 pb-4 border-b border-sky-200 shrink-0">
            {!isSelectionMode ? (
              <>
                <div className="flex flex-wrap gap-2">
                  <ToolbarButton
                    icon={<FileIcon size={24} />}
                    text="新規作成"
                    onClick={handleNewClick}
                  />
                  <ToolbarButton
                    icon={<RotateCcw size={24} />}
                    text="元に戻す"
                    onClick={handleUndo}
                    disabled={history.current <= 0}
                  />
                  <ToolbarButton
                    icon={<RotateCw size={24} />}
                    text="やり直し"
                    onClick={handleRedo}
                    disabled={history.current >= history.snapshots.length - 1}
                  />
                  <ToolbarButton
                    icon={<Plus size={24} />}
                    text="追加"
                    onClick={() => handleFileAddClick(currentPath)}
                  />
                  <ToolbarButton
                    icon={<Trash2 size={24} />}
                    text="削除"
                    onClick={handleDeleteFiles}
                    disabled={selectedFileIds.size === 0}
                  />
                </div>

                <div className="flex-grow"></div>

                <div className="flex flex-wrap gap-2 justify-end">
                  {/* ▼▼▼ FEATURE-SPLIT-ZIP (zip.js version) ▼▼▼ */}
                  <div className="flex items-center gap-2 bg-white border border-slate-300 rounded-lg p-2 shadow-sm">
                    <input
                      type="checkbox"
                      id="split-checkbox"
                      checked={isSplitEnabled}
                      onChange={(e) => setIsSplitEnabled(e.target.checked)}
                      className="form-checkbox h-5 w-5 rounded text-violet-600 transition duration-150 ease-in-out"
                    />
                    <label
                      htmlFor="split-checkbox"
                      className="text-sm font-medium text-slate-700 select-none"
                    >
                      <span className="hidden sm:inline">ファイルを分割</span>
                      <span className="inline sm:hidden">分割</span>
                    </label>
                    <input
                      type="number"
                      value={splitSize}
                      onChange={(e) =>
                        setSplitSize(Math.max(1, parseInt(e.target.value, 10)))
                      }
                      disabled={!isSplitEnabled}
                      className="w-16 py-1 px-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-400 disabled:bg-slate-100"
                    />
                    <span
                      className={`text-sm font-medium ${
                        isSplitEnabled ? "text-slate-700" : "text-slate-400"
                      }`}
                    >
                      MB
                    </span>
                  </div>
                  {/* ▲▲▲ FEATURE-SPLIT-ZIP (zip.js version) ▲▲▲ */}

                  <ActionButton
                    icon={<Archive size={18} />}
                    text="圧縮"
                    onClick={handleCompress}
                    disabled={files.length === 0}
                  />
                  <ActionButton
                    icon={<Share2 size={18} />}
                    text="共有"
                    onClick={() => setIsShareModalVisible(true)}
                    disabled={!lastCompressedInfo}
                  />
                  <div className="relative flex items-center">
                    <Shield
                      size={18}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                    />
                    <input
                      type={isPasswordVisible ? "text" : "password"}
                      placeholder="パスワード（任意）"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        setLastCompressedInfo(null);
                      }}
                      className="pl-10 pr-20 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-400 w-36 sm:w-48"
                    />
                    <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={handleGeneratePassword}
                        className="p-1 rounded text-slate-500 hover:bg-slate-200 hover:text-violet-600 transition-colors"
                        title="安全なパスワードを生成"
                      >
                        <Lock size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                        className="p-1 rounded text-slate-500 hover:bg-slate-200 transition-colors"
                        title={
                          isPasswordVisible
                            ? "パスワードを隠す"
                            : "パスワードを表示"
                        }
                      >
                        {isPasswordVisible ? (
                          <Eye size={16} />
                        ) : (
                          <EyeOff size={16} />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="w-full flex justify-end">
                <ActionButton
                  icon={<Trash2 size={18} />}
                  text="削除"
                  onClick={handleDeleteFiles}
                  disabled={selectedFileIds.size === 0}
                />
              </div>
            )}
          </div>

          <div
            onMouseDown={handleBackgroundMouseDown}
            onTouchStart={handleBackgroundTouchStart}
            onTouchEnd={handleBackgroundTouchEnd}
            onContextMenu={handleBackgroundContextMenuCleared}
            className={`interaction-target flex-1 rounded-lg overflow-y-auto ${
              files.length === 0 ? "bg-green-500/10" : "bg-white/50"
            }`}
          >
            <div className="w-full text-left text-sm">
              <div className="sticky top-0 bg-sky-100/80 backdrop-blur-sm z-10 flex items-center">
                {isSelectionMode && <div className="p-3 w-12 shrink-0"></div>}
                <div
                  className="p-3 font-bold text-slate-600 cursor-pointer hover:bg-sky-200 transition-colors flex-1 min-w-0"
                  onClick={() => !isSelectionMode && handleSort("name")}
                >
                  名前
                  {!isSelectionMode &&
                    sortConfig.key === "name" &&
                    (sortConfig.order === "asc" ? " ▲" : " ▼")}
                </div>
                <div
                  className="hidden md:flex p-3 font-bold text-slate-600 cursor-pointer hover:bg-sky-200 transition-colors w-40 items-center"
                  onClick={() => !isSelectionMode && handleSort("type")}
                >
                  種類
                  {!isSelectionMode &&
                    sortConfig.key === "type" &&
                    (sortConfig.order === "asc" ? " ▲" : " ▼")}
                </div>
                <div
                  className="flex p-3 font-bold text-slate-600 cursor-pointer hover:bg-sky-200 transition-colors w-24 justify-end items-center"
                  onClick={() => !isSelectionMode && handleSort("size")}
                >
                  サイズ
                  {!isSelectionMode &&
                    sortConfig.key === "size" &&
                    (sortConfig.order === "asc" ? " ▲" : " ▼")}
                </div>
                <div
                  className="hidden md:flex p-3 font-bold text-slate-600 cursor-pointer hover:bg-sky-200 transition-colors w-32 justify-end items-center"
                  onClick={() => !isSelectionMode && handleSort("modified")}
                >
                  更新日
                  {!isSelectionMode &&
                    sortConfig.key === "modified" &&
                    (sortConfig.order === "asc" ? " ▲" : " ▼")}
                </div>
              </div>
              <div>
                {sortedFiles.map((file) => (
                  <FileItem
                    key={file.id}
                    file={file}
                    onDownload={handleItemActivate}
                    isSelected={selectedFileIds.has(file.id)}
                    onFileSelect={(e, id) =>
                      handleFileSelect(id, e, sortedFiles)
                    }
                    onContextMenu={handleItemContextMenu}
                    dropTargetId={dropTargetId}
                    setDropTargetId={setDropTargetId}
                    onDropOnItem={handleDropOnItem}
                    isSelectionMode={isSelectionMode}
                    clipboard={clipboard}
                  />
                ))}
              </div>
            </div>
            {files.length === 0 && (
              <div className="flex flex-col items-center justify-center h-full text-slate-500 text-center p-4 pointer-events-none">
                <Upload size={48} className="mb-4" />
                {isTouchDevice ? (
                  <>
                    <p className="font-bold text-lg">
                      ファイルを追加してください
                    </p>
                    <p>
                      「追加」ボタンか、このエリアの長押しでファイルを追加します。
                    </p>
                  </>
                ) : (
                  <>
                    <p className="font-bold text-lg">
                      ファイルをドラッグ＆ドロップ
                    </p>
                    <p>
                      ファイルを追加して圧縮するか、ZIPファイルを展開します。
                    </p>
                  </>
                )}
              </div>
            )}
          </div>
          {/*===5_5.RENDER_JSX_Main_Content=End==*/}

          {/*===5_6.RENDER_JSX_Footer=Start==*/}
          <footer className="shrink-0 text-center text-xs text-slate-500 mt-2">
            <p>
              データはサーバーにアップロードされません。すべての処理はあなたのブラウザ内で完結します。
            </p>
            <p>
              &copy; 2025{" "}
              <a
                href="https://pctrouble.help/"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline"
              >
                pctrouble.help
              </a>{" "}
              All Rights Reserved.
            </p>
          </footer>
        </main>
      </div>

      {isDraggingOver && (
        <div className="hidden md:flex absolute inset-0 bg-violet-500 bg-opacity-50 items-center justify-center pointer-events-none rounded-2xl m-4 border-4 border-dashed border-white z-50">
          <div className="text-center text-white">
            <Upload size={64} className="mx-auto mb-4 animate-bounce" />
            <h2 className="text-3xl font-bold">ここにファイルをドロップ！</h2>
          </div>
        </div>
      )}
    </div>
  );
}
//===5_6.RENDER_JSX_Footer=End==
